import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { fetchNoteKeysForSet } from './lib/collectionNotes';
import { getParallelsForType, parallelKey } from './parallelData';
import { getParallelsForSet } from './sets/parallelsConfig';
import { SET_REGISTRY } from './setRegistry';
import { isScannerHostEnabled, isScannerSet } from './scannerAccess';
import { trackSetOpened, trackCardMarked } from './activityTracker';
import { CardThumbnail, imagePath, imageUrl, SerialPhotoPanel } from './CardImage';
import ShareModal from './ShareModal';
import Gallery from './Gallery';
import NavDrawer from './NavDrawer';
import CollectionModal from './CollectionModal';
import VerificationGateModal from './components/VerificationGateModal';
import SketchPanel from './SketchPanel';
import { isLight, getAccents, lightAccent } from './theme';

const STATES = [null, 'collected', 'wishlist', 'trade', 'for_sale'];
const VERIFICATION_GATE_THRESHOLD = 500;

function nextState(current) {
  const idx = STATES.indexOf(current);
  return STATES[(idx + 1) % STATES.length];
}

const STATE_STYLES_DARK = {
  collected: {
    card: { background: 'rgba(0,232,122,0.05)', border: '1px solid rgba(0,232,122,0.15)' },
    checkbox: { borderColor: '#00e87a', background: 'rgba(0,232,122,0.15)', color: '#00e87a' },
    accent: '#00e87a', icon: '✓', label: 'COLLECTED',
  },
  wishlist: {
    card: { background: 'rgba(240,192,64,0.05)', border: '1px solid rgba(240,192,64,0.2)' },
    checkbox: { borderColor: '#f0c040', background: 'rgba(240,192,64,0.12)', color: '#f0c040' },
    accent: '#f0c040', icon: '★', label: 'WISHLIST',
  },
  trade: {
    card: { background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)' },
    checkbox: { borderColor: '#00d4ff', background: 'rgba(0,212,255,0.12)', color: '#00d4ff' },
    accent: '#00d4ff', icon: '⇄', label: 'TRADE',
  },
  for_sale: {
    card: { background: 'rgba(176,96,255,0.05)', border: '1px solid rgba(176,96,255,0.2)' },
    checkbox: { borderColor: '#b060ff', background: 'rgba(176,96,255,0.12)', color: '#b060ff' },
    accent: '#b060ff', icon: '$', label: 'FOR SALE',
  },
};

// Light Side variants: same alphas, WCAG-AA accent hues (see theme.js).
const STATE_STYLES_LIGHT = {
  collected: {
    card: { background: 'rgba(0,136,74,0.05)', border: '1px solid rgba(0,136,74,0.15)' },
    checkbox: { borderColor: '#00884a', background: 'rgba(0,136,74,0.15)', color: '#00884a' },
    accent: '#00884a', icon: '✓', label: 'COLLECTED',
  },
  wishlist: {
    card: { background: 'rgba(152,112,9,0.05)', border: '1px solid rgba(152,112,9,0.2)' },
    checkbox: { borderColor: '#987009', background: 'rgba(152,112,9,0.12)', color: '#987009' },
    accent: '#987009', icon: '★', label: 'WISHLIST',
  },
  trade: {
    card: { background: 'rgba(0,127,176,0.05)', border: '1px solid rgba(0,127,176,0.2)' },
    checkbox: { borderColor: '#007fb0', background: 'rgba(0,127,176,0.12)', color: '#007fb0' },
    accent: '#007fb0', icon: '⇄', label: 'TRADE',
  },
  for_sale: {
    card: { background: 'rgba(138,58,224,0.05)', border: '1px solid rgba(138,58,224,0.2)' },
    checkbox: { borderColor: '#8a3ae0', background: 'rgba(138,58,224,0.12)', color: '#8a3ae0' },
    accent: '#8a3ae0', icon: '$', label: 'FOR SALE',
  },
};

const GRADING_COMPANIES = ['PSA', 'BGS', 'CGC', 'TAG', 'SGC', 'Other'];
const GRADE_VALUES = ['1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10'];

export default function Checklist({ user, profile, onEditProfile, setId, onBack }) {
  const setMeta = SET_REGISTRY.find(s => s.id === setId) || SET_REGISTRY[0];
  // Theme is a body class set on the profile page; this component always
  // re-renders after a change (navigation), so resolving once per render is safe.
  const AC = getAccents();
  const STATE_STYLES = isLight() ? STATE_STYLES_LIGHT : STATE_STYLES_DARK;
  const [SETS, setSETS] = useState([]);
  const [SET_PARALLELS, setSET_PARALLELS] = useState([]);
  const [SET_AUTO_PARALLELS, setSET_AUTO_PARALLELS] = useState([]);
  const [SET_INSERT_PARALLELS, setSET_INSERT_PARALLELS] = useState([]);
  const [cardStates, setCardStates] = useState({});
  const [salePrices, setSalePrices] = useState({});
  const [willingToPayMap, setWillingToPayMap] = useState({});
  const [gradings, setGradings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(() => {
    // Default open on fresh load; stays closed if user closed them this session
    const stored = sessionStorage.getItem('ck_filters_open');
    return stored === null ? true : stored === 'true';
  });

  const toggleFilters = () => {
    setFiltersOpen(prev => {
      sessionStorage.setItem('ck_filters_open', String(!prev));
      return !prev;
    });
  };
  const [search, setSearch] = useState('');
  const [activeTypeFilter, setActiveTypeFilter] = useState('all');
  const [activeStateFilter, setActiveStateFilter] = useState('all');
  const [expandedCards, setExpandedCards] = useState({});
  const [priceModal, setPriceModal] = useState(null);
  const [tradeModal, setTradeModal] = useState(null);
  // Phase 2E.1 § 5.6 Decision 6: $500+ verification gate. Wired into the
  // checklist's "pick FOR SALE → enter price" flow as a Phase A launch-prep
  // follow-up; mirrors CollectionModal's identical wiring.
  const [gateModalProps, setGateModalProps] = useState(null); // { cardKey, priceUsd } | null
  const [identityVerified, setIdentityVerified] = useState(null); // null=loading, bool once resolved
  const [conditions, setConditions] = useState({});
  const [tradeValues, setTradeValues] = useState({});
  const [tradeNotes, setTradeNotes] = useState({});
  const [photosUpdatedAtMap, setPhotosUpdatedAtMap] = useState({});
  // card_keys in this set the collector has a private note on. Keys only —
  // the note text is never pulled onto a page that may be screen-shared.
  const [noteKeys, setNoteKeys] = useState(() => new Set());
  const [conditionInput, setConditionInput] = useState('');
  const [tradeValueInput, setTradeValueInput] = useState('');
  const [tradeNotesInput, setTradeNotesInput] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [parallelFilter, setParallelFilter] = useState(() => {
    if (typeof window === 'undefined') return 'none';
    const m = window.location.hash.match(/parallel=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : 'none';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (parallelFilter === 'none') {
      // strip parallel= from hash
      const h = window.location.hash.replace(/[#&]?parallel=[^&]*/, '');
      window.history.replaceState(null, '', window.location.pathname + window.location.search + h);
    } else {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + '#parallel=' + encodeURIComponent(parallelFilter));
    }
  }, [parallelFilter]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [bulkMode, setBulkMode] = useState(null); // set.id of active bulk section
  const [bulkSelected, setBulkSelected] = useState({}); // { cardKey: true }
  const [serialPanels, setSerialPanels] = useState({});
  const [serialInputs, setSerialInputs] = useState({});
  const [serialErrors, setSerialErrors] = useState({});
  const [serialStatePicker, setSerialStatePicker] = useState(null);
  const [expandedSerial, setExpandedSerial] = useState(null);
  const [serialNewState, setSerialNewState] = useState({});
  const [sketchMeta, setSketchMeta] = useState({}); // { card_key: { subject, parallel } } for sketch entry keys

  const toggleBulkSelect = (key) => setBulkSelected(prev => ({ ...prev, [key]: !prev[key] }));
  const cancelBulk = () => { setBulkMode(null); setBulkSelected({}); };
  const bulkMark = async (status) => {
    const keys = Object.keys(bulkSelected).filter(k => bulkSelected[k]);
    if (!keys.length) return;
    if (status === null) {
      // CLEAR: delete the rows from collections, not upsert with null status
      setCardStates(prev => {
        const next = { ...prev };
        keys.forEach(k => { delete next[k]; });
        return next;
      });
      await supabase.from('collections')
        .delete()
        .eq('user_id', user.id)
        .in('card_key', keys);
    } else {
      // Mark with a status. NB: collections table has no updated_at column;
      // do not include it in the payload or Supabase rejects with PGRST204.
      const updates = {};
      keys.forEach(k => { updates[k] = status; });
      setCardStates(prev => ({ ...prev, ...updates }));
      const rows = keys.map(k => ({ user_id: user.id, card_key: k, status }));
      await supabase.from('collections').upsert(rows, { onConflict: 'user_id,card_key' });
      trackCardMarked(user.id, keys[0], status, keys.length);
    }
    cancelBulk();
  };
  const toggleSection = (setId) => setCollapsedSections(prev => ({ ...prev, [setId]: !prev[setId] })); // 'none' or parallel id
  const [gradeModal, setGradeModal] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [statePicker, setStatePicker] = useState(null);
  const [collectionModal, setCollectionModal] = useState(null); // { card, setMeta, set, mode, parallelId, existingEntry } | null
  const [zoomView, setZoomView] = useState(() => localStorage.getItem('ck_zoom_view') === 'true');
  const toggleZoom = () => setZoomView(prev => { const next = !prev; localStorage.setItem('ck_zoom_view', String(next)); if (next) { setFiltersOpen(false); } return next; });
  const [showGallery, setShowGallery] = useState(false);
  const [gradeCompany, setGradeCompany] = useState('PSA');
  const [gradeValue, setGradeValue] = useState('10');
  const [customCompany, setCustomCompany] = useState('');
  const [certNum, setCertNum] = useState('');
  const [serialNum, setSerialNum] = useState('');
  const [printRun, setPrintRun] = useState('');
  const [tagScore, setTagScore] = useState('');
  const [gradeReportUrl, setGradeReportUrl] = useState('');
  const [popThisGrade, setPopThisGrade] = useState('');
  const [popHigher, setPopHigher] = useState('');
  const saveTimer = useRef(null);
  const gradeJustSaved = useRef(false);

  // Reset filters open and parallel filter when set changes
  // Skip on initial mount so URL hash-based parallelFilter survives reload
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setFiltersOpen(true);
    sessionStorage.setItem('ck_filters_open', 'true');
    setParallelFilter('none');
  }, [setMeta.id]);

  // Load set data dynamically
  useEffect(() => {
    const loadSetData = async () => {
      try {
        const module = await import(`./sets/${setMeta.dataFile}.js`);
        setSETS(module.SETS || []);
        setSET_PARALLELS(module.PARALLELS || []);
        setSET_AUTO_PARALLELS(module.AUTO_PARALLELS || module.PARALLELS || []);
        setSET_INSERT_PARALLELS(module.INSERT_PARALLELS || module.PARALLELS || []);
      } catch(e) {
        console.error('Failed to load set data:', e);
      }
    };
    loadSetData();
  }, [setMeta.dataFile]);

  // CK 3.0 activation funnel: one set_opened per checklist visit. The source
  // ('my_checklists' / 'empty_state' / 'set_summary' / 'other') was stashed by
  // whichever screen navigated here; trackSetOpened resolves and clears it.
  useEffect(() => {
    trackSetOpened(user?.id, setMeta.id);
  }, [user?.id, setMeta.id]);

  const loadCollection = useCallback(async ({ silent = false } = {}) => {
    // silent: refresh the data maps WITHOUT toggling the full-screen `loading`
    // state. The loading early-return unmounts the whole set list, so toggling
    // it on a photo upload remounts the list and resets scroll to the top.
    if (!silent) setLoading(true);
    const { data, error } = await supabase
      .from('collections')
      .select('card_key, status, sale_price, willing_to_pay, card_condition, trade_value, trade_notes, grading_company, grading_grade, grading_cert_num, grading_serial_num, grading_print_run, grading_tag_score, grading_report_url, grading_pop_this, grading_pop_higher, has_photos, photos_updated_at, sketch_subject, sketch_parallel')
      .eq('user_id', user.id)
      .like('card_key', `${setMeta.id}__%`);
    if (!error && data) {
      const stateMap = {};
      const priceMap = {};
      const gradingMap = {};
      const condMap = {};
      const tvMap = {};
      const tnMap = {};
      const photosMap = {};
      const wtpMap = {};
      const sketchMetaMap = {};
      data.forEach(row => {
        stateMap[row.card_key] = row.status;
        if (row.sketch_subject || row.sketch_parallel) sketchMetaMap[row.card_key] = { subject: row.sketch_subject || '', parallel: row.sketch_parallel || '' };
        if (row.sale_price) priceMap[row.card_key] = row.sale_price;
        if (row.willing_to_pay) wtpMap[row.card_key] = row.willing_to_pay;
        if (row.card_condition) condMap[row.card_key] = row.card_condition;
        if (row.trade_value) tvMap[row.card_key] = row.trade_value;
        if (row.trade_notes) tnMap[row.card_key] = row.trade_notes;
        if (row.photos_updated_at) photosMap[row.card_key] = row.photos_updated_at;
        if (row.grading_company && row.grading_grade) {
          gradingMap[row.card_key] = {
            company: row.grading_company,
            grade: row.grading_grade,
            certNum: row.grading_cert_num || '',
            serialNum: row.grading_serial_num || '',
            printRun: row.grading_print_run || '',
            tagScore: row.grading_tag_score || '',
            gradeReportUrl: row.grading_report_url || '',
            popThisGrade: row.grading_pop_this || '',
            popHigher: row.grading_pop_higher || '',
          };
        }
      });
      setCardStates(stateMap);
      setSalePrices(priceMap);
      setWillingToPayMap(wtpMap);
      setGradings(gradingMap);
      setConditions(condMap);
      setTradeValues(tvMap);
      setTradeNotes(tnMap);
      setPhotosUpdatedAtMap(photosMap);
      setSketchMeta(sketchMetaMap);
    }
    // Private notes live in their own table (owner-only RLS), so this is a
    // second read. Non-fatal: a failure here costs the marker, not the page.
    try {
      setNoteKeys(await fetchNoteKeysForSet(user.id, setMeta.id));
    } catch (e) {
      console.warn('[Checklist] note keys failed (non-fatal):', e);
    }
    if (!silent) setLoading(false);
  }, [user.id, setMeta.id]);

  // Post-photo-upload refresh: re-reads the data maps (photos_updated_at cache
  // bust, has_photos, marketplace_eligible) but keeps the list mounted so the
  // user's scroll position is preserved between Front/Back uploads.
  const refreshPhotos = useCallback(() => loadCollection({ silent: true }), [loadCollection]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  // Phase 2E.1: load identity_verified once so confirmPrice can decide whether
  // a ≥$500 listing needs to be intercepted by the verification gate.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('identity_verified')
        .eq('id', user.id)
        .maybeSingle();
      if (!cancelled) setIdentityVerified(!!data?.identity_verified);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const saveToDb = useCallback(async (key, status, salePrice, gradingData, condition, tradeVal, tradeNote, isPaused = false) => {
    setSaving(true);
    if (status) {
      // Check existing has_photos before upserting so we don't overwrite it
      const { data: existing } = await supabase.from('collections')
        .select('has_photos')
        .eq('user_id', user.id)
        .eq('card_key', key)
        .single();
      const hasPhotos = existing?.has_photos || false;
      const isListed = status === 'for_sale' || status === 'trade';
      await supabase.from('collections').upsert(
        {
          user_id: user.id,
          card_key: key,
          status,
          sale_price: salePrice || null,
          grading_company: gradingData?.company || null,
          grading_grade: gradingData?.grade || null,
          grading_cert_num: gradingData?.certNum || null,
          grading_serial_num: gradingData?.serialNum || null,
          grading_print_run: gradingData?.printRun || null,
          grading_tag_score: gradingData?.tagScore || null,
          grading_report_url: gradingData?.gradeReportUrl || null,
          grading_pop_this: gradingData?.popThisGrade || null,
          grading_pop_higher: gradingData?.popHigher || null,
          card_condition: condition || null,
          trade_value: tradeVal ? parseFloat(tradeVal) : null,
          trade_notes: tradeNote || null,
          has_photos: hasPhotos,
          marketplace_eligible: isListed && hasPhotos,
          is_paused: isPaused,
        },
        { onConflict: 'user_id,card_key' }
      );
      trackCardMarked(user.id, key, status);
    } else {
      await supabase.from('collections')
        .delete()
        .eq('user_id', user.id)
        .eq('card_key', key);
    }
    setSaving(false);
  }, [user.id]);

  const saveGradingToDb = useCallback(async (key, gradingData) => {
    setSaving(true);
    const currentStatus = cardStates[key] || 'collected';
    const currentPrice = salePrices[key] || null;
    await supabase.from('collections').upsert(
      {
        user_id: user.id,
        card_key: key,
        status: currentStatus,
        sale_price: currentPrice,
        grading_company: gradingData?.company || null,
        grading_grade: gradingData?.grade || null,
      },
      { onConflict: 'user_id,card_key' }
    );
    setSaving(false);
  }, [user.id, cardStates, salePrices]);

  const selectState = useCallback((key, next) => {
    if (!key.includes("__s__")) setStatePicker(null);
    if (next === 'trade') {
      setCardStates(prev => ({ ...prev, [key]: 'trade' }));
      setConditionInput(conditions[key] || '');
      setTradeValueInput(tradeValues[key] || '');
      setTradeNotesInput(tradeNotes[key] || '');
      setTradeModal(key);
      return;
    }
    if (next === 'for_sale') {
      setCardStates(prev => ({ ...prev, [key]: 'for_sale' }));
      setConditionInput(conditions[key] || '');
      setPriceModal(key);
      setPriceInput('');
      return;
    }
    setCardStates(prev => {
      const updated = { ...prev };
      if (next) {
        updated[key] = next;
      } else {
        delete updated[key];
        setSalePrices(p => { const n = { ...p }; delete n[key]; return n; });
      }
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        if (!next && gradings[key]) {
          saveToDb(key, 'collected', null, gradings[key]);
        } else {
          saveToDb(key, next || null, null, gradings[key] || null);
        }
      }, 300);
      return updated;
    });
  }, [saveToDb, gradings]);

  const confirmPrice = useCallback(() => {
    const key = priceModal;
    const price = parseFloat(priceInput) || null;
    setSalePrices(prev => ({ ...prev, [key]: price }));
    setPriceModal(null);

    // Phase 2E.1 § 5.6 Decision 6: gate $500+ listings behind ID verification.
    // Mirrors CollectionModal's wiring — save the row paused, then open the
    // gate modal. The modal's own onPublished handler flips is_paused → false
    // on Persona success. Strictly-greater-than to match VERIFICATION_GATE_THRESHOLD
    // semantics elsewhere in the codebase (CollectionModal.js).
    const priceNum = typeof price === 'number' && price > 0 ? price : 0;
    const needsGate = priceNum > VERIFICATION_GATE_THRESHOLD && identityVerified === false;

    saveToDb(key, 'for_sale', price, gradings[key] || null, conditionInput, null, null, needsGate);

    if (needsGate) {
      setGateModalProps({ cardKey: key, priceUsd: priceNum });
    }
  }, [priceModal, priceInput, saveToDb, gradings, conditionInput, identityVerified]);

  const confirmTrade = () => {
    const key = tradeModal;
    setConditions(p => ({ ...p, [key]: conditionInput }));
    setTradeValues(p => ({ ...p, [key]: tradeValueInput }));
    setTradeNotes(p => ({ ...p, [key]: tradeNotesInput }));
    setTradeModal(null);
    saveToDb(key, 'trade', salePrices[key] || null, gradings[key] || null, conditionInput, tradeValueInput, tradeNotesInput);
  };

  const openGradeModal = useCallback((key, e) => {
    e.stopPropagation();
    const existing = gradings[key];
    if (existing) {
      setGradeCompany(GRADING_COMPANIES.includes(existing.company) ? existing.company : 'Other');
      setCustomCompany(GRADING_COMPANIES.includes(existing.company) ? '' : existing.company);
      setGradeValue(existing.grade);
      setCertNum(existing.certNum || '');
      setSerialNum(existing.serialNum || '');
      setPrintRun(existing.printRun || '');
      setTagScore(existing.tagScore || '');
      setGradeReportUrl(existing.gradeReportUrl || '');
      setPopThisGrade(existing.popThisGrade || '');
      setPopHigher(existing.popHigher || '');
    } else {
      setGradeCompany('PSA');
      setGradeValue('10');
      setCustomCompany('');
      setCertNum('');
      setSerialNum('');
      setPrintRun('');
      setTagScore('');
      setGradeReportUrl('');
      setPopThisGrade('');
      setPopHigher('');
    }
    setGradeModal(key);
  }, [gradings]);

  const confirmGrade = useCallback(async () => {
    const key = gradeModal;
    const company = gradeCompany === 'Other' ? customCompany.trim() : gradeCompany;
    if (!company) return;
    const gradingData = {
      company, grade: gradeValue,
      certNum: certNum.trim(),
      serialNum: serialNum.trim(),
      printRun: printRun.trim(),
      tagScore: tagScore.trim(),
      gradeReportUrl: gradeReportUrl.trim(),
      popThisGrade: popThisGrade.trim(),
      popHigher: popHigher.trim(),
    };
    const currentStatus = cardStates[key] || 'collected';
    const currentPrice = salePrices[key] || null;

    setGradings(prev => ({ ...prev, [key]: gradingData }));
    if (!cardStates[key]) {
      setCardStates(prev => ({ ...prev, [key]: 'collected' }));
    }
    const _isSerialKey = key.includes('__s__');
    const _serialPKey = _isSerialKey ? key.split('__s__')[0] : null;
    setTimeout(() => {
      setGradeModal(null);
      if (_isSerialKey) setStatePicker(_serialPKey);
    }, 50);

    // Save directly without relying on saveGradingToDb closure
    setSaving(true);
    const { error } = await supabase.from('collections').upsert(
      {
        user_id: user.id,
        card_key: key,
        status: currentStatus || null,
        sale_price: currentPrice,
        grading_company: gradingData.company,
        grading_grade: gradingData.grade,
        grading_cert_num: gradingData.certNum || null,
        grading_serial_num: gradingData.serialNum || null,
        grading_print_run: gradingData.printRun || null,
        grading_tag_score: gradingData.tagScore || null,
        grading_report_url: gradingData.gradeReportUrl || null,
        grading_pop_this: gradingData.popThisGrade || null,
        grading_pop_higher: gradingData.popHigher || null,
      },
      { onConflict: 'user_id,card_key' }
    );
    if (error) console.error('GRADE SAVE ERROR:', error);
    setSaving(false);
  }, [gradeModal, gradeCompany, gradeValue, customCompany, certNum, serialNum, printRun, tagScore, gradeReportUrl, popThisGrade, popHigher, cardStates, salePrices, user.id]);

  const removeGrade = useCallback((key, e) => {
    e.stopPropagation();
    setGradings(prev => { const n = { ...prev }; delete n[key]; return n; });
    setGradeModal(null);
    saveGradingToDb(key, null);
  }, [saveGradingToDb]);

  const addSerial = useCallback((pKey, printRun, selectedState) => {
    const raw = (serialInputs[pKey] || '').trim();
    const n = parseInt(raw, 10);
    if (!raw || isNaN(n) || n < 1 || n > printRun) {
      setSerialErrors(prev => ({ ...prev, [pKey]: `Enter a number between 1 and ${printRun}` }));
      return;
    }
    const existing = Object.keys(cardStates)
      .filter(k => k.startsWith(pKey + '__s__'))
      .map(k => parseInt(k.split('__s__')[1], 10));
    if (existing.includes(n)) {
      setSerialErrors(prev => ({ ...prev, [pKey]: `Serial ${n}/${printRun} already logged` }));
      return;
    }
    const serialKey = `${pKey}__s__${n}`;
    const stateToSave = selectedState || 'collected';
    setCardStates(prev => ({ ...prev, [serialKey]: stateToSave }));
    saveToDb(serialKey, stateToSave, null, null);
    setSerialInputs(prev => ({ ...prev, [pKey]: '' }));
    setSerialErrors(prev => ({ ...prev, [pKey]: '' }));
    setExpandedSerial(serialKey);
  }, [serialInputs, cardStates, saveToDb]);

  const removeSerial = useCallback(async (serialKey) => {
    setCardStates(prev => { const n = { ...prev }; delete n[serialKey]; return n; });
    saveToDb(serialKey, null, null, null);
    const frontPath = imagePath(user.id, serialKey, 'front');
    const backPath  = imagePath(user.id, serialKey, 'back');
    await supabase.storage.from('card-images').remove([frontPath, backPath]);
  }, [saveToDb, user.id]);

  // Sketch entries: unlimited copies of one artist row, keyed `${baseKey}__s__<i>`
  // where i is a meaningless copy index. max+1 so a re-added index never collides
  // with a live entry; gaps after deletes are fine.
  const addSketchEntry = useCallback(async (baseKey, selectedState) => {
    const existing = Object.keys(cardStates)
      .filter(k => k.startsWith(baseKey + '__s__'))
      .map(k => parseInt(k.split('__s__')[1], 10))
      .filter(Number.isFinite);
    let next = existing.length ? Math.max(...existing) + 1 : 1;
    const hadLegacyParent = !!cardStates[baseKey];
    if (hadLegacyParent) {
      // A sketch logged on the artist row itself (pre-multi-sketch, one per
      // artist) becomes its own numbered entry before the new one is added.
      // The card_key UPDATE moves the whole row — status, grade, sale/trade
      // fields, photo flags — and the storage move carries the photos.
      const legacyKey = `${baseKey}__s__${next}`;
      next += 1;
      setCardStates(prev => { const nxt = { ...prev, [legacyKey]: prev[baseKey] }; delete nxt[baseKey]; return nxt; });
      await supabase.from('collections').update({ card_key: legacyKey }).eq('user_id', user.id).eq('card_key', baseKey);
      await supabase.storage.from('card-images').move(imagePath(user.id, baseKey, 'front'), imagePath(user.id, legacyKey, 'front'));
      await supabase.storage.from('card-images').move(imagePath(user.id, baseKey, 'back'), imagePath(user.id, legacyKey, 'back'));
    }
    const entryKey = `${baseKey}__s__${next}`;
    const stateToSave = selectedState || 'collected';
    setCardStates(prev => ({ ...prev, [entryKey]: stateToSave }));
    await saveToDb(entryKey, stateToSave, null, null);
    setExpandedSerial(entryKey);
    // Re-sync the derived maps (gradings, photos, prices) onto the moved key
    if (hadLegacyParent) loadCollection({ silent: true });
  }, [cardStates, saveToDb, user.id, loadCollection]);

  const saveSketchMeta = useCallback(async (key, { subject, parallel }) => {
    setSketchMeta(prev => ({ ...prev, [key]: { subject: subject || '', parallel: parallel || '' } }));
    setSaving(true);
    await supabase.from('collections').upsert(
      {
        user_id: user.id,
        card_key: key,
        status: cardStates[key] || 'collected',
        sketch_subject: subject || null,
        sketch_parallel: parallel || null,
      },
      { onConflict: 'user_id,card_key' }
    );
    setSaving(false);
  }, [user.id, cardStates]);

  const removeSketchEntry = useCallback((sKey) => {
    setSketchMeta(prev => { const n = { ...prev }; delete n[sKey]; return n; });
    removeSerial(sKey);
  }, [removeSerial]);

  const toggleExpand = useCallback((key, e) => {
    e.stopPropagation();
    setExpandedCards(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const signOut = () => supabase.auth.signOut();

  const totalCards = SETS.reduce((sum, s) => sum + s.cards.length, 0);
  const collectedCount = Object.keys(cardStates).filter(k => {
    if (cardStates[k] !== 'collected') return false;
    // Don't count a base parallel key if serial children exist for it
    if (!k.includes('__s__') && Object.keys(cardStates).some(s => s.startsWith(k + '__s__'))) return false;
    return true;
  }).length;
  const wishlistCount = Object.values(cardStates).filter(s => s === 'wishlist').length;
  const tradeCount = Object.values(cardStates).filter(s => s === 'trade').length;
  const forSaleCount = Object.values(cardStates).filter(s => s === 'for_sale').length;
  const gradedCount = Object.keys(gradings).length;
  const pct = totalCards ? Math.round(
    SETS.reduce((sum, s) => sum + s.cards.filter(c => cardStates[`${s.id}__${c.n}`] === 'collected').length, 0)
    / totalCards * 100
  ) : 0;

  if (loading) return (
    <div style={styles.loadScreen}><div style={styles.loadText}>LOADING COLLECTION...</div></div>
  );

  return (
    <div style={styles.page} onClick={() => { if (!gradeJustSaved.current) setStatePicker(null); }}>
      <NavDrawer user={user} onOpenGallery={() => setShowGallery(true)} />
      <div style={styles.starsEl} />

      {/* Gallery modal */}
      {showGallery && (
        <Gallery
          user={user}
          profile={profile}
          setMeta={setMeta}
          SETS={SETS}
          cardStates={cardStates}
          sketchMeta={sketchMeta}
          onClose={() => setShowGallery(false)}
        />
      )}

      {/* Unified Collection modal */}
      {collectionModal && (
        <CollectionModal
          isOpen={true}
          user={user}
          card={collectionModal.card}
          setMeta={collectionModal.setMeta}
          set={collectionModal.set}
          mode={collectionModal.mode}
          initialParallelId={collectionModal.parallelId}
          existingEntry={collectionModal.existingEntry}
          // Refresh the data maps in place instead of reloading, so the collector
          // keeps their scroll position. CollectionModal calls onClose itself once
          // it's done — except when the $500+ verification gate opens, in which
          // case it stays mounted to host the gate and closes when that finishes.
          onSaved={() => loadCollection({ silent: true })}
          onClose={() => setCollectionModal(null)}
        />
      )}

      {/* Share modal */}
      {showShare && (
        <ShareModal
          user={user}
          profile={profile}
          cardStates={cardStates}
          salePrices={salePrices}
          gradings={gradings}
          setMeta={setMeta}
          SETS={SETS}
          SET_PARALLELS={SET_PARALLELS}
          SET_AUTO_PARALLELS={SET_AUTO_PARALLELS}
          SET_INSERT_PARALLELS={SET_INSERT_PARALLELS}
          onClose={() => setShowShare(false)}
        />
      )}

      {/* Price modal */}
      {priceModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>SET SALE PRICE</div>
            <div style={styles.modalSub}>Enter your asking price and card condition</div>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>CARD CONDITION</label>
              <select style={styles.modalSelect} value={conditionInput} onChange={e => setConditionInput(e.target.value)}>
                <option value="">Select condition...</option>
                <option value="Gem Mint">Gem Mint</option>
                <option value="Mint">Mint</option>
                <option value="Near Mint-Mint">Near Mint-Mint</option>
                <option value="Near Mint">Near Mint</option>
                <option value="Excellent-Mint">Excellent-Mint</option>
                <option value="Excellent">Excellent</option>
                <option value="Very Good-Excellent">Very Good-Excellent</option>
                <option value="Very Good">Very Good</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            <input
              style={styles.modalInput}
              type="number" min="0" step="0.01" placeholder="0.00"
              value={priceInput}
              onChange={e => setPriceInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmPrice()}
              autoFocus
            />
            <div style={styles.modalBtns}>
              <button style={styles.modalBtnSecondary} onClick={() => { setPriceModal(null); setCardStates(p => { const n = { ...p }; delete n[priceModal]; return n; }); }}>CANCEL</button>
              <button style={styles.modalBtnPrimary} onClick={confirmPrice}>CONFIRM</button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 2E.1 § 5.6 Decision 6: $500+ verification gate (renders above the checklist) */}
      {gateModalProps && (
        <VerificationGateModal
          user={user}
          cardKey={gateModalProps.cardKey}
          priceUsd={gateModalProps.priceUsd}
          onClose={() => setGateModalProps(null)}
          onPublished={() => {
            // Persona succeeded + gate unpaused the listing. Reflect that
            // locally so the next ≥$500 listing in the same session skips the gate.
            setGateModalProps(null);
            setIdentityVerified(true);
          }}
        />
      )}

      {/* Trade modal */}
      {tradeModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, borderColor: 'var(--cyan)' }}>
            <div style={{ ...styles.modalTitle, color: 'var(--cyan)' }}>⇄ TRADE DETAILS</div>
            <div style={styles.modalSub}>Add details about your trade offer</div>
            {!gradings[tradeModal] && (
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>CARD CONDITION</label>
              <select style={styles.modalSelect} value={conditionInput} onChange={e => setConditionInput(e.target.value)}>
                <option value="">Select condition...</option>
                <option value="Gem Mint">Gem Mint</option>
                <option value="Mint">Mint</option>
                <option value="Near Mint-Mint">Near Mint-Mint</option>
                <option value="Near Mint">Near Mint</option>
                <option value="Excellent-Mint">Excellent-Mint</option>
                <option value="Excellent">Excellent</option>
                <option value="Very Good-Excellent">Very Good-Excellent</option>
                <option value="Very Good">Very Good</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            )}
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>TRADE VALUE <span style={{ color: 'var(--border-2)', fontSize: '9px' }}>OPTIONAL</span></label>
              <input
                style={styles.modalInput}
                type="number"
                min="0"
                step="0.01"
                value={tradeValueInput}
                onChange={e => setTradeValueInput(e.target.value)}
                placeholder="Estimated value in USD..."
              />
            </div>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>TRADE NOTES <span style={{ color: 'var(--border-2)', fontSize: '9px' }}>OPTIONAL</span></label>
              <textarea
                style={{ ...styles.modalInput, height: '80px', resize: 'vertical' }}
                value={tradeNotesInput}
                onChange={e => setTradeNotesInput(e.target.value)}
                placeholder="What are you looking for in return?"
              />
            </div>
            <div style={styles.modalBtns}>
              <button style={styles.modalBtnSecondary} onClick={() => { setTradeModal(null); setCardStates(p => { const n = { ...p }; delete n[tradeModal]; return n; }); }}>CANCEL</button>
              <button style={{ ...styles.modalBtnPrimary, borderColor: 'var(--cyan)', color: 'var(--cyan)', background: 'rgba(var(--cyan-rgb),0.05)' }} onClick={confirmTrade}>SAVE</button>
            </div>
          </div>
        </div>
      )}

      {/* Grade modal */}
      {gradeModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, borderColor: 'var(--ice)' }}>
            <div style={{ ...styles.modalTitle, color: 'var(--ice)' }}>🏆 ADD GRADE</div>
            <div style={styles.modalSub}>Select the grading company and grade</div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ ...styles.modalField, flex: 1 }}>
                <label style={styles.modalLabel}>GRADING CO.</label>
                <select style={styles.modalSelect} value={gradeCompany} onChange={e => setGradeCompany(e.target.value)}>
                  {GRADING_COMPANIES.map(c => <option key={c} value={c}>{c === 'BGS' ? 'BGS (Beckett)' : c}</option>)}
                </select>
              </div>
              <div style={{ ...styles.modalField, flex: 1 }}>
                <label style={styles.modalLabel}>GRADE</label>
                <select style={styles.modalSelect} value={gradeValue} onChange={e => setGradeValue(e.target.value)}>
                  {GRADE_VALUES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>

            {gradeCompany === 'Other' && (
              <div style={styles.modalField}>
                <label style={styles.modalLabel}>COMPANY NAME</label>
                <input style={styles.modalInput} value={customCompany} onChange={e => setCustomCompany(e.target.value)} placeholder="Enter company name..." autoFocus />
              </div>
            )}

            {gradeCompany === 'TAG' && (
              <div style={{ background: 'rgba(var(--ice-rgb),0.04)', border: '1px solid rgba(var(--ice-rgb),0.15)', borderRadius: '2px', padding: '10px', marginBottom: '12px' }}>
                <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--ice)', marginBottom: '8px' }}>TAG SCORE</div>
                <input style={styles.modalInput} value={tagScore} onChange={e => setTagScore(e.target.value)} placeholder="0–1000 (optional)" type="number" min="0" max="1000" style={{ ...styles.modalInput, MozAppearance: 'textfield' }} />
              </div>
            )}

            <div style={styles.modalField}>
              <label style={styles.modalLabel}>CERT # <span style={{ color: 'var(--border-2)', fontSize: '9px' }}>OPTIONAL</span></label>
              <input style={styles.modalInput} value={certNum} onChange={e => setCertNum(e.target.value)} placeholder="Certificate number..." />
            </div>



            <div style={{ background: 'rgba(var(--purple-rgb),0.04)', border: '1px solid rgba(var(--purple-rgb),0.15)', borderRadius: '2px', padding: '10px', marginBottom: '12px' }}>
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--purple)', marginBottom: '8px' }}>POP REPORT <span style={{ color: 'var(--border-2)', fontSize: '8px' }}>OPTIONAL</span></div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={styles.modalLabel}>POP THIS GRADE</label>
                  <input style={styles.modalInput} value={popThisGrade} onChange={e => setPopThisGrade(e.target.value)} placeholder="e.g. 142" type="number" min="0" style={{ ...styles.modalInput, MozAppearance: 'textfield' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.modalLabel}>POP HIGHER</label>
                  <input style={styles.modalInput} value={popHigher} onChange={e => setPopHigher(e.target.value)} placeholder="e.g. 18" type="number" min="0" style={{ ...styles.modalInput, MozAppearance: 'textfield' }} />
                </div>
              </div>
            </div>

            <div style={styles.modalField}>
              <label style={styles.modalLabel}>GRADE REPORT URL <span style={{ color: 'var(--border-2)', fontSize: '9px' }}>OPTIONAL</span></label>
              <input style={{ ...styles.modalInput, color: gradeReportUrl ? 'var(--cyan)' : 'var(--text)' }} value={gradeReportUrl} onChange={e => setGradeReportUrl(e.target.value)} placeholder="https://psacard.com/cert/..." />
            </div>

            <div style={styles.gradePillPreview}>
              <div style={styles.gradePill}>
                {gradeCompany === 'Other' ? (customCompany || '?') : gradeCompany} {gradeValue}{gradeCompany === 'TAG' && tagScore ? ' · ' + tagScore : ''}
              </div>
            </div>

            <div style={styles.modalBtns}>
              <button style={styles.modalBtnSecondary} onClick={() => setGradeModal(null)}>CANCEL</button>
              {gradings[gradeModal] && (
                <button style={{ ...styles.modalBtnSecondary, color: 'var(--red)', borderColor: 'var(--red)' }} onClick={(e) => removeGrade(gradeModal, e)}>REMOVE</button>
              )}
              <button style={{ ...styles.modalBtnPrimary, borderColor: 'var(--ice)', color: 'var(--ice)', background: 'rgba(var(--ice-rgb),0.05)' }} onClick={e => { e.stopPropagation(); confirmGrade(); }}>SAVE GRADE</button>
            </div>
          </div>
        </div>
      )}

      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
              {onBack && (
                <button style={styles.backBtn} onClick={onBack}>← ALL SETS</button>
              )}

            </div>
            <div style={styles.logoLine}>{setMeta.brand} · {setMeta.category} · {setMeta.year}</div>
            <h1 style={styles.title}>{setMeta.shortName.toUpperCase()}</h1>
            {setMeta.checklistCredit && (
              <div style={styles.checklistCredit}>
                CHECKLIST COURTESY OF{' '}
                <a href={`/u/${setMeta.checklistCredit}`} style={styles.checklistCreditLink}>@{setMeta.checklistCredit}</a>
              </div>
            )}
            {setMeta.photoCredit && (
              <div style={styles.checklistCredit}>
                TEMPORARY PHOTOS COURTESY OF{' '}
                <span style={styles.checklistCreditLink}>@{setMeta.photoCredit.handle}</span>
                {' '}FROM THIS{' '}
                <a href={setMeta.photoCredit.sourceUrl} target="_blank" rel="noopener noreferrer" style={styles.checklistCreditLink}>SOURCE</a>
              </div>
            )}

            {isScannerHostEnabled() && isScannerSet(setMeta.id) && (
              <button
                onClick={() => { window.history.pushState({}, '', `/scan-ai-card?from=${setMeta.id}`); window.dispatchEvent(new PopStateEvent('popstate')); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(var(--green-rgb),0.1)', color: 'var(--green)', border: '1px solid var(--green)', borderRadius: '6px', padding: '9px 14px', fontFamily: "'Orbitron', monospace", fontWeight: 700, fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer', marginTop: '16px' }}
              >
                SCAN A CARD
                <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '8px', letterSpacing: '1px', color: 'var(--bg)', background: 'var(--green)', padding: '1px 5px', borderRadius: '3px' }}>BETA</span>
              </button>
            )}
          </div>

        </div>
        <div style={styles.headerLine} />
      </header>

      {!zoomView && <div style={styles.statsBar}>
        <div style={{ ...styles.pill, ...styles.pillCyan }}>TOTAL: {totalCards}</div>
        <div style={{ ...styles.pill, ...styles.pillGreen }}>✓ {collectedCount}</div>
        <div style={{ ...styles.pill, ...styles.pillGold }}>★ {wishlistCount}</div>
        <div style={{ ...styles.pill, ...styles.pillBlue }}>⇄ {tradeCount}</div>
        <div style={{ ...styles.pill, ...styles.pillPurple }}>$ {forSaleCount}</div>
        <div style={{ ...styles.pill, ...styles.pillIce }}>🏆 GRADED: {gradedCount}</div>
        <div style={{ ...styles.pill, ...styles.pillGold }}>COMPLETE: {pct}%</div>
        {saving && <div style={{ ...styles.pill, ...styles.pillMuted }}>SAVING...</div>}
      </div>}

      {!zoomView && <div style={styles.progressWrap}>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${pct}%` }} />
        </div>
      </div>}

      <div style={styles.controls}>
        <input style={styles.searchBox} value={search} onChange={e => setSearch(e.target.value)} placeholder="SEARCH CARDS..." />
      </div>

      {/* Collapsible filter accordion */}
      <div style={styles.accordionWrap}>
        <button style={styles.accordionToggle} onClick={toggleFilters}>
          <span style={styles.accordionLabel}>FILTERS</span>
          <span style={styles.accordionMeta}>
            {!filtersOpen && (
              <span style={styles.accordionSummary}>
                {activeStateFilter !== 'all' ? activeStateFilter.toUpperCase().replace('_',' ') : 'ALL'}
                {' · '}{activeTypeFilter !== 'all' ? activeTypeFilter.toUpperCase() : 'ALL SETS'}
                {parallelFilter !== 'none' ? ` · ${parallelFilter.toUpperCase()}` : ''}
              </span>
            )}
            <span style={styles.accordionChevron}>{filtersOpen ? '▲' : '▼'}</span>
          </span>
        </button>

        {filtersOpen && (
          <div style={styles.accordionBody}>
            <div style={styles.filterTabs}>
              <span style={styles.filterLabel}>VIEW:</span>
              {[
                { id: 'all', label: 'ALL' },
                { id: 'none', label: 'UNCHECKED' },
                { id: 'collected', label: '✓ COLLECTED' },
                { id: 'wishlist', label: '★ WISHLIST' },
                { id: 'trade', label: '⇄ TRADE' },
                { id: 'for_sale', label: '$ FOR SALE' },
              ].map(f => (
                <button key={f.id} style={activeStateFilter === f.id ? { ...styles.filterTab, ...styles.filterTabActive } : styles.filterTab} onClick={() => setActiveStateFilter(f.id)}>{f.label}</button>
              ))}
            </div>

            <div style={styles.filterTabs}>
              <span style={styles.filterLabel}>SET:</span>
              {[
                { id: 'all', label: 'ALL' },
                { id: 'base', label: 'BASE SET' },
                { id: 'insert', label: 'INSERTS' },
                { id: 'auto', label: 'AUTOGRAPHS' },
                { id: 'sketch', label: 'SKETCH' },
              ].map(f => (
                <button key={f.id} style={activeTypeFilter === f.id ? { ...styles.filterTab, ...styles.filterTabActiveType } : styles.filterTab} onClick={() => setActiveTypeFilter(f.id)}>{f.label}</button>
              ))}
            </div>

            <div style={{ ...styles.filterTabs, borderBottom: 'none' }}>
              <span style={styles.filterLabel}>PARALLEL:</span>
              <select
                style={styles.parallelSelect}
                value={parallelFilter}
                onChange={e => setParallelFilter(e.target.value)}
              >
                <option value="none">ALL CARDS</option>
                {SET_PARALLELS.some(p => p.group)
                  ? [...new Set(SET_PARALLELS.map(p => p.group))].map(group => (
                      <optgroup key={group} label={`── ${group} ──`}>
                        {SET_PARALLELS.filter(p => p.group === group).map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name}{p.print_run ? (p.print_run === 1 ? ' 1/1' : ` /${p.print_run}`) : ''}{p.odds ? ` (${p.odds})` : ''}
                          </option>
                        ))}
                      </optgroup>
                    ))
                  : (
                      <optgroup label="── PARALLELS ──">
                        {SET_PARALLELS.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name}{p.print_run ? (p.print_run === 1 ? ' 1/1' : ` /${p.print_run}`) : ''}
                          </option>
                        ))}
                      </optgroup>
                    )
                }
              </select>
            </div>
          </div>
        )}
      </div>

      <main style={styles.main}>
        {SETS.filter(set => activeTypeFilter === 'all' || set.type === activeTypeFilter).map(set => {
          const _sp = set.type === 'auto' ? SET_AUTO_PARALLELS : set.type === 'insert' ? SET_INSERT_PARALLELS : SET_PARALLELS;
          const parallels = set.parallels || (_sp.length > 0 ? _sp : getParallelsForType(set.type));
          const hasParallels = parallels.length > 0;
          const q = search.toLowerCase();

          // When parallel filter is active, skip sets with no parallels
          if (parallelFilter !== 'none' && !hasParallels) return null;
          const filteredParallel = parallelFilter !== 'none' ? parallels.find(p => p.id === parallelFilter) : null;

          const visibleCards = set.cards.filter(card => {
            const matchSearch = !q || card.name.toLowerCase().includes(q) || (card.artist || '').toLowerCase().includes(q) || card.n.toLowerCase().includes(q) || (card.label || '').toLowerCase().includes(q);
            const key = `${setMeta.id}__${set.id}__${card.n}`;
            const state = cardStates[key] || null;
            const pKey2 = parallelFilter !== 'none' ? parallelKey(`${setMeta.id}__${set.id}`, card.n, parallelFilter) : null;
            const pState2 = pKey2 ? (cardStates[pKey2] || null) : null;
            const serialStates2 = pKey2 ? Object.keys(cardStates).filter(k => k.startsWith(pKey2 + '__s__')).map(k => cardStates[k]) : [];
            const hasMatchingSerial = activeStateFilter !== 'all' && activeStateFilter !== 'none' && serialStates2.includes(activeStateFilter);
            const hasMatchingSerialGrade = activeStateFilter === 'graded' && pKey2 && Object.keys(cardStates).filter(k => k.startsWith(pKey2 + '__s__')).some(k => !!gradings[k]);
            // When no parallel filter, also check all parallel/serial keys for this card
            const cardPrefix = `${setMeta.id}__${set.id}__${card.n}`;
            const allCardKeys = Object.keys(cardStates).filter(k => k === cardPrefix || k.startsWith(cardPrefix + '__'));
            const anyKeyMatchesState = activeStateFilter !== 'all' && activeStateFilter !== 'none' && allCardKeys.some(k => cardStates[k] === activeStateFilter);
            const anyKeyMatchesGrade = activeStateFilter === 'graded' && allCardKeys.some(k => !!gradings[k]);
            const matchState = parallelFilter !== 'none'
              ? (activeStateFilter === 'all' || (activeStateFilter === 'none' && !pState2 && serialStates2.length === 0) || pState2 === activeStateFilter || hasMatchingSerial || (activeStateFilter === 'graded' && (!!gradings[pKey2] || hasMatchingSerialGrade)))
              : (activeStateFilter === 'all' || (activeStateFilter === 'none' && !state) || state === activeStateFilter || anyKeyMatchesState || (activeStateFilter === 'graded' && (!!gradings[key] || anyKeyMatchesGrade)));
            return matchSearch && matchState;
          });
          if (visibleCards.length === 0) return null;
          const setTotal = set.cards.length;
          const setDone = set.cards.filter(c => {
            const k = `${setMeta.id}__${set.id}__${c.n}`;
            if (cardStates[k] === 'collected') return true;
            // A sketch artist row is done once any entry is logged for it
            return set.type === 'sketch' && Object.keys(cardStates).some(x => x.startsWith(k + '__s__'));
          }).length;

          return (
            <div key={set.id} style={styles.section}>
              <div style={{ ...styles.sectionHeader, cursor: 'pointer' }} onClick={() => toggleSection(set.id)}>
                <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '14px', color: typeColor(set.type), marginRight: '10px', transition: 'transform 0.15s', display: 'inline-block', transform: collapsedSections[set.id] ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</div>
                <div style={{ ...styles.sectionTitle, color: typeColor(set.type) }}>{set.title}</div>
                <div style={styles.sectionProg}>{setDone}/{setTotal}</div>
              </div>
              {!collapsedSections[set.id] && (() => {
                const isBulk = bulkMode === set.id;
                const sectionKeys = visibleCards.map(card => `${setMeta.id}__${set.id}__${card.n}`);
                const selectedInSection = sectionKeys.filter(k => bulkSelected[k]);
                const allSelected = sectionKeys.length > 0 && selectedInSection.length === sectionKeys.length;
                return (
                  <>
                  <div style={{ position: 'sticky', top: 0, zIndex: 8, background: isBulk ? 'var(--bulk-bg)' : 'var(--panel-deep)', borderBottom: isBulk ? '2px solid var(--cyan)' : '1px solid var(--border)', padding: '7px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => {
                      if (!isBulk) { setBulkMode(set.id); setBulkSelected({}); }
                      else {
                        const next = {};
                        sectionKeys.forEach(k => { next[k] = !allSelected; });
                        setBulkSelected(next);
                      }
                    }}>
                      <div style={{ width: '14px', height: '14px', border: isBulk ? '1px solid var(--cyan)' : '1px solid var(--border-2)', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'var(--cyan)', background: isBulk && allSelected ? 'rgba(var(--cyan-rgb),0.15)' : 'transparent' }}>{isBulk && allSelected ? '✓' : ''}</div>
                      <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '2px', color: isBulk ? 'var(--cyan)' : 'var(--border-2)' }}>{isBulk ? (selectedInSection.length > 0 ? `${selectedInSection.length} SELECTED` : 'SELECT ALL') : 'BULK MARK'}</span>
                    </div>
                    {isBulk && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => bulkMark('collected')} style={{ fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', padding: '4px 8px', border: '1px solid var(--green)', color: 'var(--green)', background: 'rgba(var(--green-rgb),0.08)', borderRadius: '2px', cursor: 'pointer' }}>✓ COLLECTED</button>
                        <button onClick={() => bulkMark('wishlist')} style={{ fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', padding: '4px 8px', border: '1px solid var(--gold)', color: 'var(--gold)', background: 'rgba(var(--gold-rgb),0.08)', borderRadius: '2px', cursor: 'pointer' }}>★ WISHLIST</button>
                        <button onClick={() => bulkMark('trade')} style={{ fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', padding: '4px 8px', border: '1px solid var(--cyan)', color: 'var(--cyan)', background: 'rgba(var(--cyan-rgb),0.08)', borderRadius: '2px', cursor: 'pointer' }}>⇄ TRADE</button><button onClick={() => bulkMark(null)} style={{ fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', padding: '4px 8px', border: '1px solid var(--red)', color: 'var(--red)', background: 'rgba(var(--red-rgb),0.08)', borderRadius: '2px', cursor: 'pointer' }}>✕ CLEAR</button>
                        <button onClick={cancelBulk} style={{ fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', padding: '4px 8px', border: '1px solid var(--border-2)', color: 'var(--muted-2)', background: 'transparent', borderRadius: '2px', cursor: 'pointer' }}>✕</button>
                      </div>
                    )}
                  </div>
                  <div style={zoomView ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(480px, 100%), 1fr))', gap: '6px' } : styles.cardGrid}>
                {visibleCards.map(card => {
                  const key = `${setMeta.id}__${set.id}__${card.n}`;
                  const state = cardStates[key] || null;
                  // Sketch artist rows (roster-style sub-sets with no parallels): unlimited
                  // entries live at `${key}__s__<i>`. The row renders as collected whenever
                  // any entry exists, but `state` stays raw so the CollectionModal opener
                  // keeps its edit/new mode. Real-ladder sketch sub-sets (Stellar Signatures,
                  // Smugglers) have parallels and are excluded by !hasParallels.
                  const isSketchRow = set.type === 'sketch' && !hasParallels;
                  const sketchKeys = isSketchRow ? Object.keys(cardStates).filter(k => k.startsWith(key + '__s__')) : [];
                  const displayState = isSketchRow && sketchKeys.length > 0 ? 'collected' : state;
                  const st = displayState ? STATE_STYLES[displayState] : null;
                  const cardPrefix2 = `${setMeta.id}__${set.id}__${card.n}`;
                  const parallelMatchesFilter = activeStateFilter !== 'all' && activeStateFilter !== 'none' && parallelFilter === 'none' && state !== activeStateFilter && Object.keys(cardStates).filter(k => k !== cardPrefix2 && k.startsWith(cardPrefix2 + '__')).some(k => cardStates[k] === activeStateFilter || (activeStateFilter === 'graded' && !!gradings[k]));
                  const isExpanded = expandedCards[key] || parallelMatchesFilter;
                  const grading = gradings[key] || null;
                  const parallelCollectedCount = hasParallels ? parallels.filter(p => {
                    const pk = parallelKey(`${setMeta.id}__${set.id}`, card.n, p.id);
                    if (cardStates[pk]) return true;
                    if (p.print_run && p.print_run > 1) return Object.keys(cardStates).some(k => k.startsWith(pk + '__s__'));
                    return false;
                  }).length : 0;

                  // When parallel filter is active, show only the parallel card row
                  if (parallelFilter !== 'none' && filteredParallel) {
                    const pKey = parallelKey(`${setMeta.id}__${set.id}`, card.n, filteredParallel.id);
                    const isSerializedParallel = filteredParallel.print_run && filteredParallel.print_run > 1;
                    const serialKeys = isSerializedParallel
                      ? Object.keys(cardStates).filter(k => k.startsWith(pKey + '__s__'))
                      : [];
                    const hasSerials = serialKeys.length > 0;
                    const pState = isSerializedParallel
                      ? (hasSerials ? 'collected' : (cardStates[pKey] || null))
                      : (cardStates[pKey] || null);
                    const pSt = pState ? STATE_STYLES[pState] : null;
                    const pGrading = gradings[pKey] || null;
                    const serialPanelOpen = !!serialPanels[pKey];
                    return (
                      <div key={pKey + '_wrap'} style={{display:'flex',flexDirection:'column'}}>
                      <div style={zoomView ? { ...(pSt ? { ...styles.cardItem, ...pSt.card } : styles.cardItem), minHeight: '80px', alignItems: 'center' } : (pSt ? { ...styles.cardItem, ...pSt.card } : styles.cardItem)}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (gradeJustSaved.current) return;
                          setStatePicker(prev => prev === pKey ? null : pKey);
                        }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '2px', background: pSt ? pSt.accent : lightAccent(filteredParallel.color), opacity: pSt ? 1 : 0.3 }} />
                        <div style={pSt ? { ...styles.checkbox, ...pSt.checkbox } : styles.checkbox}>{pSt ? pSt.icon : ''}</div>
                        <div
                          onClickCapture={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setStatePicker(null);
                            setCollectionModal({
                              mode: pState ? 'edit' : 'new',
                              card,
                              setMeta,
                              set,
                              parallelId: filteredParallel.id,
                              existingEntry: pState ? {
                                cardKey: pKey,
                                status: pState,
                                sale_price: salePrices[pKey],
                                willing_to_pay: willingToPayMap[pKey],
                                trade_value: tradeValues[pKey],
                                trade_notes: tradeNotes[pKey],
                                grading_serial_num: gradings[pKey]?.serial_num,
                                grading_company: gradings[pKey]?.company,
                                grading_grade: gradings[pKey]?.grade,
                                grading_cert_num: gradings[pKey]?.cert_num,
                                grading_tag_score: gradings[pKey]?.tag_score,
                                grading_print_run: gradings[pKey]?.print_run,
                                grading_pop_this: gradings[pKey]?.pop_this,
                                grading_pop_higher: gradings[pKey]?.pop_higher,
                                grading_report_url: gradings[pKey]?.report_url,
                                photos_updated_at: photosUpdatedAtMap[pKey],
                              } : undefined,
                            });
                          }}
                          style={{ cursor: 'pointer' }}
                        ><CardThumbnail userId={user.id} cardKey={pKey} isOwner={true} zoom={zoomView} stateColor={pSt ? pSt.accent : null} photosUpdatedAt={photosUpdatedAtMap[pKey]} onImageChange={refreshPhotos} /></div>
                        <div style={styles.cardInfo}>
                          <div style={zoomView ? { ...styles.cardNum, fontSize: '16px' } : styles.cardNum}>{card.label || card.n}</div>
                          <div style={zoomView ? { ...(pState === 'collected' ? { ...styles.cardName, ...styles.cardNameDone } : styles.cardName), fontSize: '15px' } : (pState === 'collected' ? { ...styles.cardName, ...styles.cardNameDone } : styles.cardName)}>{card.name}</div>
                          {card.artist && <div style={zoomView ? { ...styles.cardArtist, fontSize: '16px' } : styles.cardArtist}>{card.artist}</div>}
                          {pState === 'for_sale' && salePrices[pKey] && <div style={styles.priceTag}>${Number(salePrices[pKey]).toFixed(2)}</div>}
                        </div>
                        <div style={styles.cardRight}>
                          <div style={{ ...styles.stateTag, color: lightAccent(filteredParallel.color), borderColor: lightAccent(filteredParallel.color) + '66', marginBottom: '2px', ...(zoomView ? { fontSize: '13px', padding: '5px 10px', letterSpacing: '1.5px' } : {}) }}>{filteredParallel.name}</div>
                          {pState && <div style={{ ...styles.stateTag, color: pSt.accent, borderColor: pSt.accent + '66', ...(zoomView ? { fontSize: '13px', padding: '5px 10px', letterSpacing: '1.5px' } : {}) }}>{pSt.label}</div>}
                          <div style={styles.bottomBtns}>
                            {!hasSerials && !pGrading && <button style={zoomView ? { ...styles.gradeBtn, fontSize: '13px', padding: '5px 10px' } : styles.gradeBtn} onClick={(e) => openGradeModal(pKey, e)} title="Add grade">GRADE</button>}
                            {!hasSerials && pGrading && <div style={zoomView ? { ...styles.gradingBadge, fontSize: '14px', padding: '5px 12px' } : styles.gradingBadge} onClick={(e) => openGradeModal(pKey, e)}>{pGrading.company} {pGrading.grade}</div>}
                          </div>
                        </div>
                      </div>
                      {(!isSerializedParallel || !hasSerials) && statePicker === pKey && (
                        <div style={styles.statePicker} onClick={e => e.stopPropagation()}>
                          {[
                            {id:'collected', icon:'✓', label:'COLLECTED', color:AC.green},
                            {id:'wishlist',  icon:'★', label:'WISHLIST',  color:AC.gold},
                            {id:'trade',     icon:'⇄', label:'TRADE',     color:AC.cyan},
                            {id:'for_sale',  icon:'$', label:'FOR SALE',  color:AC.purple},
                          ].map(opt => (
                            <button key={opt.id}
                              style={{...styles.statePickerBtn,
                                borderColor: pState===opt.id ? opt.color : 'var(--border-3)',
                                color: pState===opt.id ? opt.color : 'var(--muted-3)',
                                background: pState===opt.id ? opt.color+'22' : 'transparent'}}
                              onClick={() => selectState(pKey, pState===opt.id ? null : opt.id)}>
                              <span>{opt.icon}</span>
                              <span style={{fontSize:'8px',letterSpacing:'0px',whiteSpace:'nowrap'}}>{opt.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {isSerializedParallel && statePicker === pKey && (
                        <div style={{ background: 'var(--drawer-2)', border: '1px solid var(--border)', borderTop: 'none', padding: '10px' }} onClick={e => e.stopPropagation()}>
                          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: 'var(--gold)', background: 'rgba(var(--gold-rgb),0.06)', border: '1px solid rgba(var(--gold-rgb),0.2)', borderRadius: '2px', padding: '6px 8px', marginBottom: '8px', lineHeight: 1.5 }}>
                            ★ Add a serial # to appear in the Community Checklist. Optional.
                          </div>
                          {serialKeys.sort((a, b) => parseInt(a.split('__s__')[1]) - parseInt(b.split('__s__')[1])).map(sKey => {
                                      const sNum = sKey.split('__s__')[1];
                                      const sState = cardStates[sKey] || 'collected';
                                      const sSt = STATE_STYLES[sState];
                                      const sGrading = gradings[sKey] || null;
                                      const isSerialExpanded = expandedSerial === sKey;
                                      return (
                                        <div key={sKey} style={{ marginBottom: '6px' }}>
                                          <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'6px 8px',
                                            border: `1px solid ${isSerialExpanded ? 'var(--cyan)' : sSt.accent+'44'}`,
                                            borderRadius: isSerialExpanded ? '3px 3px 0 0' : '2px',
                                            borderBottom: isSerialExpanded ? 'none' : undefined,
                                            cursor:'pointer', background: isSerialExpanded ? 'rgba(var(--cyan-rgb),0.03)' : 'transparent' }}
                                            onClick={e => { e.stopPropagation(); if (!isSerialExpanded) setExpandedSerial(sKey); }}>
                                            <div onClick={e => e.stopPropagation()}>
                                              <div
                                              onClickCapture={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setStatePicker(null);
                                                setExpandedSerial(null);
                                                // Derive parallelId: sKey looks like "setPath__cardNum__parallel__<pid>__s__<num>"
                                                const baseKey = sKey.split('__s__')[0];
                                                const baseSegments = baseKey.split('__');
                                                const pid = baseSegments[baseSegments.length - 1];
                                                setCollectionModal({
                                                  mode: 'edit',
                                                  card,
                                                  setMeta,
                                                  set,
                                                  parallelId: pid,
                                                  existingEntry: {
                                                    cardKey: sKey,
                                                    status: sState,
                                                    sale_price: salePrices[sKey],
                                                    willing_to_pay: willingToPayMap[sKey],
                                                    trade_value: tradeValues[sKey],
                                                    trade_notes: tradeNotes[sKey],
                                                    grading_serial_num: sNum,
                                                    grading_company: sGrading?.company,
                                                    grading_grade: sGrading?.grade,
                                                    grading_cert_num: sGrading?.cert_num,
                                                    grading_tag_score: sGrading?.tag_score,
                                                    grading_pop_this: sGrading?.pop_this,
                                                    grading_pop_higher: sGrading?.pop_higher,
                                                    grading_report_url: sGrading?.report_url,
                                                    photos_updated_at: photosUpdatedAtMap[sKey],
                                                  },
                                                });
                                              }}
                                              style={{ cursor: 'pointer' }}
                                            ><CardThumbnail userId={user.id} cardKey={sKey} isOwner={true} zoom={false} stateColor={sSt.accent} photosUpdatedAt={photosUpdatedAtMap[sKey]} onImageChange={refreshPhotos} /></div>
                                            </div>
                                            <div style={{ flex:1, minWidth:0 }}>
                                              <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'12px', color: isSerialExpanded ? 'var(--cyan)' : sSt.accent }}>{sNum}/{filteredParallel.print_run}</div>
                                              <div style={{ display:'flex', gap:'4px', marginTop:'2px', flexWrap:'wrap' }}>
                                                <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'0.05em', color:sSt.accent, border:`1px solid ${sSt.accent}44`, padding:'1px 5px', borderRadius:'2px' }}>{sSt.icon} {sState.replace('_',' ').toUpperCase()}</span>
                                                {sGrading && <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'0.05em', color:'var(--cyan)', border:'1px solid rgba(var(--cyan-rgb),0.3)', padding:'1px 5px', borderRadius:'2px', background:'rgba(var(--cyan-rgb),0.08)' }}>{sGrading.company} {sGrading.grade}</span>}
                                              </div>
                                            </div>
                                            
                                            <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'11px', color:'var(--muted-2)', cursor:'pointer', padding:'0 2px' }}
                                              onClick={e => { e.stopPropagation(); removeSerial(sKey); }}>✕</span>
                                          </div>
                                          {isSerialExpanded && (
                                            <div style={{ border:'1px solid var(--cyan)', borderTop:'none', borderRadius:'0 0 3px 3px', overflow:'hidden' }} onClick={e => e.stopPropagation()}>
                                              <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                                                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'6px' }}>STATUS</div>
                                                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'4px' }}>
                                                  {[
                                                    {id:'collected', icon:'✓', label:'COLLECTED', color:AC.green},
                                                    {id:'wishlist',  icon:'★', label:'WISHLIST',  color:AC.gold},
                                                    {id:'trade',     icon:'⇄', label:'TRADE',     color:AC.cyan},
                                                    {id:'for_sale',  icon:'$',  label:'FOR SALE',  color:AC.purple},
                                                  ].map(opt => (
                                                    <button key={opt.id}
                                                      style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'6px 2px', gap:'2px',
                                                        border:`1px solid ${sState===opt.id ? opt.color : 'var(--border)'}`,
                                                        borderRadius:'2px', cursor:'pointer', background: sState===opt.id ? opt.color+'22' : 'transparent' }}
                                                      onClick={() => selectState(sKey, sState===opt.id ? null : opt.id)}>
                                                      <span style={{ fontSize:'12px', color: sState===opt.id ? opt.color : 'var(--muted-2)' }}>{opt.icon}</span>
                                                      <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'5px', letterSpacing:'.06em', color: sState===opt.id ? opt.color : 'var(--muted-2)' }}>{opt.label}</span>
                                                    </button>
                                                  ))}
                                                </div>
                                              </div>
                                              <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                                                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'8px' }}>CARD PHOTOS</div>
                                                <div onClick={e => e.stopPropagation()}>
                                                  <SerialPhotoPanel userId={user.id} cardKey={sKey} stateColor={sSt.accent} photosUpdatedAt={photosUpdatedAtMap[sKey]} onImageChange={refreshPhotos} listingStatus={sState} />
                                                </div>
                                              </div>
                                              <div style={{ padding:'8px 10px' }}>
                                                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'6px' }}>GRADE</div>
                                                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                                  {sGrading ? (
                                                    <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'10px', color:'var(--cyan)', border:'1px solid rgba(var(--cyan-rgb),0.4)', background:'rgba(var(--cyan-rgb),0.08)', padding:'4px 10px', borderRadius:'2px', cursor:'pointer' }}
                                                      onClick={e => openGradeModal(sKey, e)}>🏆 {sGrading.company} {sGrading.grade}</span>
                                                  ) : (
                                                    <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'10px', color:'var(--muted-2)', flex:1 }}>No grade logged</span>
                                                  )}
                                                  <button style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.08em', padding:'6px 12px', borderRadius:'2px', border:'1px solid var(--muted-2)', color:'var(--muted-2)', background:'transparent', cursor:'pointer' }}
                                                    onClick={e => openGradeModal(sKey, e)}>
                                                    {sGrading ? 'EDIT' : '+ ADD GRADE'}
                                                  </button>
                                                </div>
                                              </div>
                                              <div style={{ padding:'6px 10px', borderTop:'1px solid var(--border)', textAlign:'right' }}>
                                                <button style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.08em', padding:'4px 10px', borderRadius:'2px', border:'1px solid rgba(var(--cyan-rgb),0.3)', color:'var(--cyan)', background:'transparent', cursor:'pointer' }}
                                                  onClick={e => { e.stopPropagation(); setExpandedSerial(null); }}>CLOSE ✕</button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                            <input
                              type="number" min="1" max={filteredParallel.print_run}
                              value={serialInputs[pKey] || ''}
                              onChange={e => { setSerialInputs(prev => ({ ...prev, [pKey]: e.target.value })); setSerialErrors(prev => ({ ...prev, [pKey]: '' })); }}
                              placeholder={`Serial # (1-${filteredParallel.print_run})`}
                              style={{ flex: 1, background: 'var(--panel)', border: '1px solid rgba(var(--cyan-rgb),0.2)', borderRadius: '2px', padding: '6px 10px', fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: 'var(--text)', outline: 'none' }}
                            />
                            <button
                              onClick={() => addSerial(pKey, filteredParallel.print_run, serialNewState[pKey])}
                              style={{ fontFamily: "'Orbitron', monospace", fontSize: '8px', fontWeight: 700, letterSpacing: '0.08em', padding: '6px 12px', borderRadius: '2px', border: 'none', background: 'var(--green)', color: 'var(--bg)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              + ADD
                            </button>
                          </div>
                          {serialErrors[pKey] && (
                            <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: 'var(--red-2)', marginTop: '4px' }}>{serialErrors[pKey]}</div>
                          )}
                          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '8px', color: 'var(--muted-2)', marginTop: '5px' }}>
                            {serialKeys.length > 0 ? `${serialKeys.length === 1 ? 'Serial' : 'Serials'} ${serialKeys.map(k => k.split('__s__')[1]).sort((a,b) => parseInt(a) - parseInt(b)).join(', ')}/${filteredParallel.print_run}${serialKeys.length > 1 ? ` · ${serialKeys.length} logged` : ' logged'}` : `0 of ${filteredParallel.print_run} serials logged`}
                          </div>
                        </div>
                      )}
                      </div>
                    );
                  }

                  return (
                    <div key={key} style={styles.cardWrapper}>
                      <div style={zoomView ? { ...(st ? { ...styles.cardItem, ...st.card } : styles.cardItem), minHeight: '80px', alignItems: 'center' } : (st ? { ...styles.cardItem, ...st.card } : styles.cardItem)} onClick={(e) => { e.stopPropagation(); if (bulkMode === set.id) { toggleBulkSelect(key); } else { setStatePicker(prev => prev === key ? null : key); } }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '2px', background: st ? st.accent : typeColor(set.type), opacity: st ? 1 : 0.3 }} />
                        {bulkMode === set.id
                          ? <div style={{ width: '16px', height: '16px', border: '1px solid var(--cyan)', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: 'var(--cyan)', background: bulkSelected[key] ? 'rgba(var(--cyan-rgb),0.15)' : 'transparent', flexShrink: 0 }}>{bulkSelected[key] ? '✓' : ''}</div>
                          : <div style={st ? { ...styles.checkbox, ...st.checkbox } : styles.checkbox}>{st ? st.icon : ''}</div>
                        }
                        <div
                          onClickCapture={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setStatePicker(null);
                            setCollectionModal({
                              mode: state ? 'edit' : 'new',
                              card,
                              setMeta,
                              set,
                              parallelId: 'base',
                              existingEntry: state ? {
                                cardKey: key,
                                status: state,
                                sale_price: salePrices[key],
                                willing_to_pay: willingToPayMap[key],
                                trade_value: tradeValues[key],
                                trade_notes: tradeNotes[key],
                                grading_serial_num: gradings[key]?.serial_num,
                                grading_company: gradings[key]?.company,
                                grading_grade: gradings[key]?.grade,
                                grading_cert_num: gradings[key]?.cert_num,
                                grading_tag_score: gradings[key]?.tag_score,
                                grading_print_run: gradings[key]?.print_run,
                                grading_pop_this: gradings[key]?.pop_this,
                                grading_pop_higher: gradings[key]?.pop_higher,
                                grading_report_url: gradings[key]?.report_url,
                                photos_updated_at: photosUpdatedAtMap[key],
                              } : undefined,
                            });
                          }}
                          style={{ cursor: 'pointer' }}
                        ><CardThumbnail userId={user.id} cardKey={key} isOwner={true} zoom={zoomView} stateColor={st ? st.accent : null} photosUpdatedAt={photosUpdatedAtMap[key]} onImageChange={refreshPhotos} /></div>
                        <div style={styles.cardInfo}>
                          <div style={zoomView ? { ...styles.cardNum, fontSize: '16px' } : styles.cardNum}>{card.label || card.n}</div>
                          <div style={zoomView ? { ...(displayState === 'collected' ? { ...styles.cardName, ...styles.cardNameDone } : styles.cardName), fontSize: '15px' } : (displayState === 'collected' ? { ...styles.cardName, ...styles.cardNameDone } : styles.cardName)}>{card.name}</div>
                          {card.artist && <div style={zoomView ? { ...styles.cardArtist, fontSize: '16px' } : styles.cardArtist}>{card.artist}</div>}
                          {state === 'for_sale' && salePrices[key] && <div style={styles.priceTag}>${Number(salePrices[key]).toFixed(2)}</div>}
                          {hasParallels && parallelCollectedCount > 0 && <div style={styles.parallelBadge}>+{parallelCollectedCount} parallel{parallelCollectedCount !== 1 ? 's' : ''}</div>}
                          {isSketchRow && sketchKeys.length > 0 && <div style={styles.parallelBadge}>{sketchKeys.length} sketch{sketchKeys.length !== 1 ? 'es' : ''}</div>}
                          {noteKeys.has(key) && <div style={styles.noteBadge} title="You have a private note on this card">🔒 NOTE</div>}
                        </div>
                        <div style={styles.cardRight}>
                          {displayState && <div style={{ ...styles.stateTag, color: st.accent, borderColor: st.accent + '66', ...(zoomView ? { fontSize: '13px', padding: '5px 10px', letterSpacing: '1.5px' } : {}) }}>{st.label}</div>}
                          {grading && (
                            <div style={zoomView ? { ...styles.gradingBadge, fontSize: '14px', padding: '5px 12px' } : styles.gradingBadge} onClick={(e) => openGradeModal(key, e)}>
                              {grading.company} {grading.grade}
                            </div>
                          )}
                          <div style={styles.bottomBtns}>
                            {!grading && !Object.keys(cardStates).some(k => k.startsWith(key + '__') && k.includes('__s__')) && <button style={zoomView ? { ...styles.gradeBtn, fontSize: '13px', padding: '5px 10px' } : styles.gradeBtn} onClick={(e) => openGradeModal(key, e)} title="Add grade">GRADE</button>}
                          </div>
                          {hasParallels && (
                            <button style={zoomView ? { ...styles.expandBtn, color: isExpanded ? 'var(--gold)' : 'var(--muted-2)', borderColor: isExpanded ? 'var(--gold)' : 'var(--border-3)', fontSize: '13px', padding: '5px 10px' } : { ...styles.expandBtn, color: isExpanded ? 'var(--gold)' : 'var(--muted-2)', borderColor: isExpanded ? 'var(--gold)' : 'var(--border-3)' }} onClick={(e) => toggleExpand(key, e)}>
                              {isExpanded ? '▾ PARALLELS' : '▸ PARALLELS'}
                            </button>
                          )}
                        </div>
                      </div>

                      {statePicker === key && (!isSketchRow || sketchKeys.length === 0) && (
          <div style={styles.statePicker} onClick={e => e.stopPropagation()}>
            {[
              {id:'collected', icon:'✓', label:'COLLECTED', color:AC.green},
              {id:'wishlist',  icon:'★', label:'WISHLIST',  color:AC.gold},
              {id:'trade',     icon:'⇄', label:'TRADE',     color:AC.cyan},
              {id:'for_sale',  icon:'$', label:'FOR SALE',  color:AC.purple},
            ].map(opt => (
              <button key={opt.id}
                style={{...styles.statePickerBtn,
                  borderColor: state===opt.id ? opt.color : 'var(--border-3)',
                  color: state===opt.id ? opt.color : 'var(--muted-3)',
                  background: state===opt.id ? opt.color+'22' : 'transparent'}}
                onClick={() => selectState(key, state===opt.id ? null : opt.id)}>
                <span>{opt.icon}</span>
                <span style={{fontSize:'8px',letterSpacing:'0px',whiteSpace:'nowrap'}}>{opt.label}</span>
              </button>
            ))}
          </div>
        )}
        {isSketchRow && (() => {
          // State filters auto-open the panel when an entry matches (mirrors serialHasFilterMatch)
          const sketchFilterMatch = activeStateFilter !== 'all' && activeStateFilter !== 'none' && sketchKeys.some(k => cardStates[k] === activeStateFilter || (activeStateFilter === 'graded' && !!gradings[k]));
          if (statePicker !== key && !sketchFilterMatch) return null;
          return (
            <SketchPanel
              baseKey={key}
              sketchKeys={sketchKeys}
              cardStates={cardStates}
              gradings={gradings}
              sketchMeta={sketchMeta}
              photosUpdatedAtMap={photosUpdatedAtMap}
              expandedSerial={expandedSerial}
              setExpandedSerial={setExpandedSerial}
              selectState={selectState}
              openGradeModal={openGradeModal}
              onAdd={(s) => addSketchEntry(key, s)}
              onRemove={removeSketchEntry}
              onSaveMeta={saveSketchMeta}
              userId={user.id}
              refreshPhotos={refreshPhotos}
              sketchParallels={set.sketchParallels}
              stateStyles={STATE_STYLES}
            />
          );
        })()}
        {isExpanded && hasParallels && (
                        <div style={styles.parallelsWrap}>
                          <div style={styles.parallelsGrid}>
                            {parallels.map(parallel => {
                              const pKey = parallelKey(`${setMeta.id}__${set.id}`, card.n, parallel.id);
                              const isSerializedParallel = parallel.print_run && parallel.print_run > 1;
                              const serialKeys = isSerializedParallel
                                ? Object.keys(cardStates).filter(k => k.startsWith(pKey + '__s__'))
                                : [];
                              const hasSerials = serialKeys.length > 0;
                              const pState = isSerializedParallel
                                ? (hasSerials ? 'collected' : (cardStates[pKey] || null))
                                : (cardStates[pKey] || null);
                              const pSt = pState ? STATE_STYLES[pState] : null;
                              const pGrading = gradings[pKey] || null;
                              const serialHasFilterMatch = isSerializedParallel && activeStateFilter !== 'all' && activeStateFilter !== 'none' && serialKeys.some(k => cardStates[k] === activeStateFilter || (activeStateFilter === 'graded' && !!gradings[k]));
                              const serialPanelOpen = !!serialPanels[pKey] || serialHasFilterMatch;
                              return (
                                <div key={pKey} style={{ display: 'flex', flexDirection: 'column' }}>
                                <div
                                  style={pSt ? { ...styles.parallelItem, ...pSt.card } : styles.parallelItem}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (gradeJustSaved.current) return;
                                    setStatePicker(prev => prev === pKey ? null : pKey);
                                  }}>
                                  <div style={{ width: '3px', height: '3px', borderRadius: '50%', flexShrink: 0, marginTop: '5px', background: lightAccent(parallel.color), boxShadow: `0 0 4px ${lightAccent(parallel.color)}` }} />
                                  <div style={styles.parallelInfo}>
                                    <div style={{ ...styles.parallelName, color: pState ? pSt.accent : lightAccent(parallel.color) }}>{parallel.name}</div>
                                    {parallel.print_run && <div style={styles.parallelPrint}>{parallel.print_run === 1 ? '1/1' : `#'d /${parallel.print_run}`}</div>}
                                    {isSerializedParallel && hasSerials && (
                                      <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: 'var(--green)' }}>
                                        {serialKeys.length} serial{serialKeys.length > 1 ? 's' : ''} logged
                                      </div>
                                    )}
                                    {pState === 'for_sale' && salePrices[pKey] && <div style={styles.parallelPrice}>${Number(salePrices[pKey]).toFixed(2)}</div>}
                                  </div>
                                  <div style={styles.parallelRight}>
                                    <div style={pSt ? { ...styles.parallelCheck, ...pSt.checkbox } : styles.parallelCheck}>{pSt ? pSt.icon : ''}</div>
                                    {pGrading
                                      ? <div style={{ ...styles.gradingBadge, fontSize: '8px' }} onClick={(e) => openGradeModal(pKey, e)}>{pGrading.company} {pGrading.grade}</div>
                                      : <button style={{ ...styles.gradeBtn, fontSize: '7px', padding: '2px 6px' }} onClick={(e) => openGradeModal(pKey, e)}>GRADE</button>
                                    }
                                    <div
                                      onClickCapture={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setStatePicker(null);
                                        setCollectionModal({
                                          mode: pState ? 'edit' : 'new',
                                          card,
                                          setMeta,
                                          set,
                                          parallelId: parallel.id,
                                          existingEntry: pState ? {
                                            cardKey: pKey,
                                            status: pState,
                                            sale_price: salePrices[pKey],
                                            willing_to_pay: willingToPayMap[pKey],
                                            trade_value: tradeValues[pKey],
                                            trade_notes: tradeNotes[pKey],
                                            grading_serial_num: gradings[pKey]?.serial_num,
                                            grading_company: gradings[pKey]?.company,
                                            grading_grade: gradings[pKey]?.grade,
                                            grading_cert_num: gradings[pKey]?.cert_num,
                                            grading_tag_score: gradings[pKey]?.tag_score,
                                            grading_print_run: gradings[pKey]?.print_run,
                                            grading_pop_this: gradings[pKey]?.pop_this,
                                            grading_pop_higher: gradings[pKey]?.pop_higher,
                                            grading_report_url: gradings[pKey]?.report_url,
                                            photos_updated_at: photosUpdatedAtMap[pKey],
                                          } : undefined,
                                        });
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    ><CardThumbnail userId={user.id} cardKey={pKey} isOwner={true} zoom={false} stateColor={pState ? pSt.accent : null} photosUpdatedAt={photosUpdatedAtMap[pKey]} onImageChange={refreshPhotos} /></div>
                                  </div>
                                </div>
                                {(!isSerializedParallel || !hasSerials) && statePicker === pKey && (
                                  <div style={styles.statePicker} onClick={e => e.stopPropagation()}>
                                    {[
                                      {id:'collected', icon:'✓', label:'COLLECTED', color:AC.green},
                                      {id:'wishlist',  icon:'★', label:'WISHLIST',  color:AC.gold},
                                      {id:'trade',     icon:'⇄', label:'TRADE',     color:AC.cyan},
                                      {id:'for_sale',  icon:'$', label:'FOR SALE',  color:AC.purple},
                                    ].map(opt => (
                                      <button key={opt.id}
                                        style={{...styles.statePickerBtn,
                                          borderColor: pState===opt.id ? opt.color : 'var(--border-3)',
                                          color: pState===opt.id ? opt.color : 'var(--muted-3)',
                                          background: pState===opt.id ? opt.color+'22' : 'transparent'}}
                                        onClick={() => selectState(pKey, pState===opt.id ? null : opt.id)}>
                                        <span>{opt.icon}</span>
                                        <span style={{fontSize:'8px',letterSpacing:'0px',whiteSpace:'nowrap'}}>{opt.label}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {isSerializedParallel && (statePicker === pKey || serialPanelOpen) && (
                                  <div style={{ background: 'var(--drawer-2)', border: '1px solid var(--border)', borderTop: 'none', padding: '10px', marginTop: '0px' }} onClick={e => e.stopPropagation()}>
                                    {!serialHasFilterMatch && <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: 'var(--gold)', background: 'rgba(var(--gold-rgb),0.06)', border: '1px solid rgba(var(--gold-rgb),0.2)', borderRadius: '2px', padding: '6px 8px', marginBottom: '8px', lineHeight: 1.5 }}>
                                      ★ Add a serial # to appear in the Community Checklist. Optional.
                                    </div>}
                                    {serialKeys.sort((a, b) => parseInt(a.split('__s__')[1]) - parseInt(b.split('__s__')[1])).map(sKey => {
                                      const sNum = sKey.split('__s__')[1];
                                      const sState = cardStates[sKey] || 'collected';
                                      const sSt = STATE_STYLES[sState];
                                      const sGrading = gradings[sKey] || null;
                                      const isSerialExpanded = expandedSerial === sKey;
                                      return (
                                        <div key={sKey} style={{ marginBottom: '6px' }}>
                                          <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'6px 8px',
                                            border: `1px solid ${isSerialExpanded ? 'var(--cyan)' : sSt.accent+'44'}`,
                                            borderRadius: isSerialExpanded ? '3px 3px 0 0' : '2px',
                                            borderBottom: isSerialExpanded ? 'none' : undefined,
                                            cursor:'pointer', background: isSerialExpanded ? 'rgba(var(--cyan-rgb),0.03)' : 'transparent' }}
                                            onClick={e => { e.stopPropagation(); if (!isSerialExpanded) setExpandedSerial(sKey); }}>
                                            <div onClick={e => e.stopPropagation()}>
                                              <div
                                              onClickCapture={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setStatePicker(null);
                                                setExpandedSerial(null);
                                                // Derive parallelId: sKey looks like "setPath__cardNum__parallel__<pid>__s__<num>"
                                                const baseKey = sKey.split('__s__')[0];
                                                const baseSegments = baseKey.split('__');
                                                const pid = baseSegments[baseSegments.length - 1];
                                                setCollectionModal({
                                                  mode: 'edit',
                                                  card,
                                                  setMeta,
                                                  set,
                                                  parallelId: pid,
                                                  existingEntry: {
                                                    cardKey: sKey,
                                                    status: sState,
                                                    sale_price: salePrices[sKey],
                                                    willing_to_pay: willingToPayMap[sKey],
                                                    trade_value: tradeValues[sKey],
                                                    trade_notes: tradeNotes[sKey],
                                                    grading_serial_num: sNum,
                                                    grading_company: sGrading?.company,
                                                    grading_grade: sGrading?.grade,
                                                    grading_cert_num: sGrading?.cert_num,
                                                    grading_tag_score: sGrading?.tag_score,
                                                    grading_pop_this: sGrading?.pop_this,
                                                    grading_pop_higher: sGrading?.pop_higher,
                                                    grading_report_url: sGrading?.report_url,
                                                    photos_updated_at: photosUpdatedAtMap[sKey],
                                                  },
                                                });
                                              }}
                                              style={{ cursor: 'pointer' }}
                                            ><CardThumbnail userId={user.id} cardKey={sKey} isOwner={true} zoom={false} stateColor={sSt.accent} photosUpdatedAt={photosUpdatedAtMap[sKey]} onImageChange={refreshPhotos} /></div>
                                            </div>
                                            <div style={{ flex:1, minWidth:0 }}>
                                              <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'12px', color: isSerialExpanded ? 'var(--cyan)' : sSt.accent }}>{sNum}/{parallel.print_run}</div>
                                              <div style={{ display:'flex', gap:'4px', marginTop:'2px', flexWrap:'wrap' }}>
                                                <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'0.05em', color:sSt.accent, border:`1px solid ${sSt.accent}44`, padding:'1px 5px', borderRadius:'2px' }}>{sSt.icon} {sState.replace('_',' ').toUpperCase()}</span>
                                                {sGrading && <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'0.05em', color:'var(--cyan)', border:'1px solid rgba(var(--cyan-rgb),0.3)', padding:'1px 5px', borderRadius:'2px', background:'rgba(var(--cyan-rgb),0.08)' }}>{sGrading.company} {sGrading.grade}</span>}
                                              </div>
                                            </div>
                                            
                                            <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'11px', color:'var(--muted-2)', cursor:'pointer', padding:'0 2px' }}
                                              onClick={e => { e.stopPropagation(); removeSerial(sKey); }}>✕</span>
                                          </div>
                                          {isSerialExpanded && (
                                            <div style={{ border:'1px solid var(--cyan)', borderTop:'none', borderRadius:'0 0 3px 3px', overflow:'hidden' }} onClick={e => e.stopPropagation()}>
                                              <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                                                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'6px' }}>STATUS</div>
                                                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'4px' }}>
                                                  {[
                                                    {id:'collected', icon:'✓', label:'COLLECTED', color:AC.green},
                                                    {id:'wishlist',  icon:'★', label:'WISHLIST',  color:AC.gold},
                                                    {id:'trade',     icon:'⇄', label:'TRADE',     color:AC.cyan},
                                                    {id:'for_sale',  icon:'$',  label:'FOR SALE',  color:AC.purple},
                                                  ].map(opt => (
                                                    <button key={opt.id}
                                                      style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'6px 2px', gap:'2px',
                                                        border:`1px solid ${sState===opt.id ? opt.color : 'var(--border)'}`,
                                                        borderRadius:'2px', cursor:'pointer', background: sState===opt.id ? opt.color+'22' : 'transparent' }}
                                                      onClick={() => selectState(sKey, sState===opt.id ? null : opt.id)}>
                                                      <span style={{ fontSize:'12px', color: sState===opt.id ? opt.color : 'var(--muted-2)' }}>{opt.icon}</span>
                                                      <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'5px', letterSpacing:'.06em', color: sState===opt.id ? opt.color : 'var(--muted-2)' }}>{opt.label}</span>
                                                    </button>
                                                  ))}
                                                </div>
                                              </div>
                                              <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                                                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'8px' }}>CARD PHOTOS</div>
                                                <div onClick={e => e.stopPropagation()}>
                                                  <SerialPhotoPanel userId={user.id} cardKey={sKey} stateColor={sSt.accent} photosUpdatedAt={photosUpdatedAtMap[sKey]} onImageChange={refreshPhotos} listingStatus={sState} />
                                                </div>
                                              </div>
                                              <div style={{ padding:'8px 10px' }}>
                                                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'6px' }}>GRADE</div>
                                                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                                  {sGrading ? (
                                                    <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'10px', color:'var(--cyan)', border:'1px solid rgba(var(--cyan-rgb),0.4)', background:'rgba(var(--cyan-rgb),0.08)', padding:'4px 10px', borderRadius:'2px', cursor:'pointer' }}
                                                      onClick={e => openGradeModal(sKey, e)}>🏆 {sGrading.company} {sGrading.grade}</span>
                                                  ) : (
                                                    <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'10px', color:'var(--muted-2)', flex:1 }}>No grade logged</span>
                                                  )}
                                                  <button style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.08em', padding:'6px 12px', borderRadius:'2px', border:'1px solid var(--muted-2)', color:'var(--muted-2)', background:'transparent', cursor:'pointer' }}
                                                    onClick={e => openGradeModal(sKey, e)}>
                                                    {sGrading ? 'EDIT' : '+ ADD GRADE'}
                                                  </button>
                                                </div>
                                              </div>
                                              <div style={{ padding:'6px 10px', borderTop:'1px solid var(--border)', textAlign:'right' }}>
                                                <button style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.08em', padding:'4px 10px', borderRadius:'2px', border:'1px solid rgba(var(--cyan-rgb),0.3)', color:'var(--cyan)', background:'transparent', cursor:'pointer' }}
                                                  onClick={e => { e.stopPropagation(); setExpandedSerial(null); }}>CLOSE ✕</button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                                      <input
                                        type="number" min="1" max={parallel.print_run}
                                        value={serialInputs[pKey] || ''}
                                        onChange={e => { setSerialInputs(prev => ({ ...prev, [pKey]: e.target.value })); setSerialErrors(prev => ({ ...prev, [pKey]: '' })); }}
                                        placeholder={`Serial # (1-${parallel.print_run})`}
                                        style={{ flex: 1, background: 'var(--panel)', border: '1px solid rgba(var(--cyan-rgb),0.2)', borderRadius: '2px', padding: '6px 10px', fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: 'var(--text)', outline: 'none' }}
                                      />
                                      <button
                                        onClick={() => addSerial(pKey, parallel.print_run, serialNewState[pKey])}
                                        style={{ fontFamily: "'Orbitron', monospace", fontSize: '8px', fontWeight: 700, letterSpacing: '0.08em', padding: '6px 12px', borderRadius: '2px', border: 'none', background: 'var(--green)', color: 'var(--bg)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        + ADD
                                      </button>
                                    </div>
                                    {serialErrors[pKey] && (
                                      <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: 'var(--red-2)', marginTop: '4px' }}>{serialErrors[pKey]}</div>
                                    )}
                                    <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '8px', color: 'var(--muted-2)', marginTop: '5px' }}>
                                      {serialKeys.length > 0 ? `${serialKeys.length === 1 ? 'Serial' : 'Serials'} ${serialKeys.map(k => k.split('__s__')[1]).sort((a,b) => parseInt(a) - parseInt(b)).join(', ')}/${parallel.print_run}${serialKeys.length > 1 ? ` · ${serialKeys.length} logged` : ' logged'}` : `0 of ${parallel.print_run} serials logged`}
                                    </div>
                                  </div>
                                )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
                  </>
                );
              })()}
            </div>
          );
        })}
      </main>

      {/* Zoom toggle bottom bar */}
      <div style={styles.zoomBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '16px' }}>🔍</span>
          <span style={styles.zoomBarLabel}>ZOOM VIEW</span>
        </div>
        <div style={{ ...styles.zoomTrack, ...(zoomView ? styles.zoomTrackOn : {}) }} onClick={toggleZoom}>
          <div style={{ ...styles.zoomThumb, ...(zoomView ? styles.zoomThumbOn : {}) }} />
        </div>
      </div>
      <footer style={{ borderTop: '1px solid var(--border)', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '3px', color: 'var(--text)' }}>© 2026 COLLECTIONKEEPER.ORG</div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <a href="/deployments" style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--text)', textDecoration: 'none' }}>Deployments</a>
          <a href="/terms" style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--text)', textDecoration: 'none' }}>Terms & Privacy</a>
        </div>
      </footer>
      <div style={{ height: '80px' }} />
    </div>
  );
}

function typeColor(type) {
  switch (type) {
    case 'base': return 'var(--cyan)';
    case 'insert': return 'var(--purple)';
    case 'auto': return 'var(--gold)';
    case 'sketch': return 'var(--sketch-orange)';
    default: return 'var(--brand-gold)';
  }
}

const styles = {
  page: { minHeight: '100vh', overflowX: 'hidden', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Rajdhani', sans-serif", position: 'relative' },
  starsEl: { position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)', backgroundImage: `radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.5) 0%, transparent 100%), radial-gradient(1px 1px at 30% 50%, rgba(255,255,255,0.3) 0%, transparent 100%), radial-gradient(1.5px 1.5px at 50% 10%, rgba(255,255,255,0.6) 0%, transparent 100%), radial-gradient(1px 1px at 70% 70%, rgba(255,255,255,0.4) 0%, transparent 100%)`, pointerEvents: 'none', zIndex: 0 },
  loadScreen: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  loadText: { fontFamily: "'Orbitron', monospace", fontSize: '13px', letterSpacing: '4px', color: 'var(--muted-2)' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  zoomBar: { position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50, background: 'var(--panel-deep)', borderTop: '1px solid var(--border)', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' },
  zoomBarLabel: { fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted)' },
  zoomTrack: { width: '44px', height: '24px', borderRadius: '12px', background: 'var(--border)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s' },
  zoomTrackOn: { background: 'rgba(var(--cyan-rgb),0.25)', border: '1px solid var(--cyan)' },
  zoomThumb: { width: '18px', height: '18px', borderRadius: '50%', background: 'var(--muted-2)', position: 'absolute', top: '3px', left: '3px', transition: 'left 0.2s, background 0.2s' },
  zoomThumbOn: { left: '23px', background: 'var(--cyan)' },
  modal: { background: 'var(--panel-deep)', border: '1px solid var(--purple)', borderRadius: '4px', padding: '32px', width: '100%', maxWidth: '380px' },
  modalTitle: { fontFamily: "'Orbitron', monospace", fontSize: '14px', letterSpacing: '2px', color: 'var(--purple)', marginBottom: '8px' },
  modalSub: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted-3)', marginBottom: '20px' },
  modalField: { marginBottom: '16px' },
  modalLabel: { display: 'block', fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted)', marginBottom: '6px' },
  modalInput: { width: '100%', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono', monospace", fontSize: '14px', padding: '10px 14px', borderRadius: '2px', outline: 'none', boxSizing: 'border-box' },
  modalSelect: { width: '100%', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono', monospace", fontSize: '14px', padding: '10px 14px', borderRadius: '2px', outline: 'none', boxSizing: 'border-box', cursor: 'pointer' },
  gradePillPreview: { display: 'flex', justifyContent: 'center', margin: '16px 0' },
  gradePill: { fontFamily: "'Orbitron', monospace", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', color: 'var(--ice)', padding: '6px 16px', border: '1px solid var(--ice)', borderRadius: '4px', background: 'rgba(var(--ice-rgb),0.08)' },
  modalBtns: { display: 'flex', gap: '8px', marginTop: '8px' },
  modalBtnPrimary: { flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '1px', padding: '10px', border: '1px solid var(--purple)', background: 'rgba(var(--purple-rgb),0.1)', color: 'var(--purple)', cursor: 'pointer', borderRadius: '2px' },
  modalBtnSecondary: { flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '1px', padding: '10px', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', borderRadius: '2px' },
  header: { position: 'relative', zIndex: 10, background: 'var(--bg-header)', padding: '24px 24px 0', borderBottom: '1px solid var(--border)' },
  headerInner: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', maxWidth: '1400px', margin: '0 auto', paddingBottom: '20px' },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Orbitron', monospace", fontSize: 'var(--fs-back-btn)', letterSpacing: '2px', color: 'var(--muted)', padding: '0 0 8px', display: 'block' },
  logoLine: { fontFamily: "'Orbitron', monospace", fontSize: 'var(--fs-logo-line)', letterSpacing: '5px', color: 'var(--brand-gold)', opacity: 0.8, marginBottom: '8px' },
  title: { fontFamily: "'Orbitron', monospace", fontSize: 'clamp(18px, 3vw, 32px)', fontWeight: 900, color: 'var(--text-strong)', margin: 0, letterSpacing: '1px', textShadow: 'var(--title-glow)' },
  checklistCredit: { fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '3px', color: 'var(--muted-4)', marginTop: '10px', textTransform: 'uppercase' },
  checklistCreditLink: { color: 'var(--brand-gold)', textDecoration: 'none', fontWeight: 700 },
  titleAccent: { color: 'var(--gold)' },
  headerLine: { height: '1px', background: 'linear-gradient(90deg, transparent, var(--brand-gold), var(--cyan), var(--brand-gold), transparent)' },
  userBar: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  profileLink: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--gold)', textDecoration: 'none', padding: '7px 12px', border: '1px solid rgba(var(--gold-rgb),0.3)', borderRadius: '2px' },
  signOutBtn: { fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '1px', padding: '7px 12px', background: 'transparent', border: '1px solid var(--border-2)', color: 'var(--muted)', cursor: 'pointer', borderRadius: '2px' },
  statsBar: { position: 'sticky', top: 0, zIndex: 9, display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', padding: '12px 20px', background: 'var(--bg-sticky)', borderBottom: '1px solid var(--border)' },
  pill: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-pill)', fontWeight: 'var(--fw-pill)', padding: '5px 12px', borderRadius: '2px', border: '1px solid var(--border)', color: 'var(--muted-2)' },
  pillCyan: { borderColor: 'var(--cyan)', color: 'var(--cyan)' },
  pillGreen: { borderColor: 'var(--green)', color: 'var(--green)' },
  pillGold: { borderColor: 'var(--brand-gold)', color: 'var(--brand-gold)' },
  pillBlue: { borderColor: 'var(--cyan)', color: 'var(--cyan)' },
  pillPurple: { borderColor: 'var(--purple)', color: 'var(--purple)' },
  pillIce: { borderColor: 'var(--ice)', color: 'var(--ice)' },
  pillMuted: { borderColor: 'var(--border-2)', color: 'var(--muted)' },
  progressWrap: { padding: '0 20px 10px', background: 'var(--bg-sticky)', position: 'sticky', top: '54px', zIndex: 8, borderBottom: '1px solid var(--border)' },
  progressTrack: { height: '3px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, var(--cyan), var(--gold))', borderRadius: '2px', transition: 'width 0.3s ease' },
  controls: { display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '14px 20px', borderBottom: '1px solid var(--border)' },
  searchBox: { flex: 1, minWidth: '0', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-search)', padding: '8px 14px', borderRadius: '2px', outline: 'none' },
  accordionWrap: { borderBottom: '1px solid var(--border)' },
  accordionToggle: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 20px', background: 'transparent', border: 'none',
    cursor: 'pointer', borderBottom: '1px solid var(--border)',
  },
  accordionLabel: { fontFamily: "'Orbitron', monospace", fontSize: 'var(--fs-accordion-label)', letterSpacing: '3px', color: 'var(--muted)' },
  accordionMeta: { display: 'flex', alignItems: 'center', gap: '10px' },
  accordionSummary: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-accordion-label)', color: 'var(--muted-2)' },
  accordionChevron: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--muted-2)' },
  accordionBody: { borderBottom: '1px solid var(--border)' },
  filterTabs: { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', padding: '8px 20px', borderBottom: '1px solid var(--border)' },
  filterLabel: { fontFamily: "'Orbitron', monospace", fontSize: 'var(--fs-filter-label)', letterSpacing: '2px', color: 'var(--muted)', marginRight: '4px' },
  parallelSelect: { background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-parallel-select)', padding: '5px 10px', borderRadius: '2px', outline: 'none', cursor: 'pointer', minWidth: '0', width: '100%' },
  filterTab: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-filter-tab)', fontWeight: 'var(--fw-filter-tab)', letterSpacing: '1px', padding: '5px 12px', borderRadius: '2px', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' },
  filterTabActive: { borderColor: 'var(--green)', color: 'var(--green)', background: 'rgba(var(--green-rgb),0.06)' },
  filterTabActiveType: { borderColor: 'var(--gold)', color: 'var(--gold)', background: 'rgba(var(--gold-rgb),0.06)' },
  main: { position: 'relative', zIndex: 1, padding: '20px 20px 80px', maxWidth: '1400px', margin: '0 auto' },
  section: { marginBottom: '32px' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid var(--border)' },
  sectionTitle: { fontFamily: "'Orbitron', monospace", fontSize: 'var(--fs-section-title)', fontWeight: 'var(--fw-section-title)', letterSpacing: '3px', flex: 1 },
  sectionProg: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-section-prog)', fontWeight: 'var(--fw-section-prog)', color: 'var(--green)' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '6px' },
  cardWrapper: { display: 'flex', flexDirection: 'column' },
  cardItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 12px', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '2px', cursor: 'pointer', transition: 'all 0.15s', position: 'relative', overflow: 'visible' },
  checkbox: { width: '16px', height: '16px', flexShrink: 0, border: '1px solid var(--muted-2)', borderRadius: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '1px', fontSize: '11px', color: 'var(--muted-2)' },
  cardInfo: { flex: 1, minWidth: 0 },
  cardNum: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-card-num)', color: 'var(--muted)', marginBottom: '2px' },
  cardName: { fontFamily: "'Rajdhani', sans-serif", fontWeight: 'var(--fw-card-name)', fontSize: 'var(--fs-card-name)', color: 'var(--text)', lineHeight: 1.2 },
  cardNameDone: { color: 'var(--text-done)' },
  cardArtist: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-card-artist)', color: 'var(--muted)', marginTop: '2px' },
  priceTag: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-price-tag)', fontWeight: 'var(--fw-price-tag)', color: 'var(--purple)', marginTop: '3px' },
  parallelBadge: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--gold)', marginTop: '3px' },
  // Private-note marker. Same slot pattern as priceTag/parallelBadge so it can
  // never collide with GRADE / PARALLELS in the right-hand control stack.
  noteBadge: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--muted)', marginTop: '3px' },
  cardRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 },
  stateTag: { fontFamily: "'Orbitron', monospace", fontSize: 'var(--fs-state-tag)', letterSpacing: '1px', padding: '2px 5px', border: '1px solid', borderRadius: '1px' },
  gradingBadge: {
    fontFamily: "'Orbitron', monospace", fontSize: 'var(--fs-grading-badge)', fontWeight: 700, letterSpacing: '1px',
    padding: '2px 6px', border: '1px solid var(--ice)', borderRadius: '3px',
    color: 'var(--ice)', background: 'rgba(var(--ice-rgb),0.08)',
    cursor: 'pointer',
  },
  bottomBtns: { display: 'flex', gap: '4px', alignItems: 'center' },
  gradeBtn: { background: 'none', border: '1px solid var(--muted-2)', color: 'var(--muted-2)', cursor: 'pointer', fontSize: '10px', padding: '2px 6px', lineHeight: 1, borderRadius: '2px', fontFamily: "'Orbitron', monospace", letterSpacing: '1px' },
  expandBtn: { background: 'none', border: '1px solid', cursor: 'pointer', fontSize: 'var(--fs-expand-btn)', padding: '3px 7px', lineHeight: 1, borderRadius: '2px', fontFamily: "'Orbitron', monospace", letterSpacing: '1px', marginTop: '3px' },
  statePicker: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '4px',
    padding: '6px 8px',
    background: 'var(--panel-deep2)',
    border: '1px solid var(--border)',
    borderTop: 'none',
    borderRadius: '0 0 4px 4px',
  },
  statePickerBtn: {
    flex: '1 1 0',
    minWidth: 0,
    width: '25%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '3px',
    padding: '8px 2px',
    border: '1px solid',
    borderRadius: '3px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: "'Orbitron', monospace",
    fontSize: '14px',
    transition: 'all 0.15s',
    boxSizing: 'border-box',
  },
  parallelsWrap: { background: 'var(--drawer)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 2px 2px', padding: '8px 10px' },
  parallelsGrid: { display: 'flex', flexDirection: 'column', gap: '4px' },
  parallelItem: { display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', borderRadius: '2px', border: '1px solid transparent', cursor: 'pointer', transition: 'all 0.15s', minHeight: '56px' },
  parallelInfo: { flex: 1, minWidth: 0 },
  parallelName: { fontFamily: "'Rajdhani', sans-serif", fontWeight: 'var(--fw-parallel-name)', fontSize: 'var(--fs-parallel-name)', lineHeight: 1.2 },
  parallelPrint: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-parallel-print)', color: 'var(--muted)' },
  parallelPrice: { fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: 'var(--purple)' },
  parallelGrading: { fontFamily: "'Orbitron', monospace", fontSize: '8px', color: 'var(--ice)', marginTop: '2px' },
  parallelRight: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' },
  parallelCheck: { width: '14px', height: '14px', flexShrink: 0, border: '1px solid var(--muted-2)', borderRadius: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'var(--muted-2)' },
};